import { type LayoutNode } from '@backend/lib/db/typeRoots/layoutNode'
import { type RouterOutput, trpc } from '@/config/trpc'
import { useEffect, useMemo, useRef, useState } from 'react'
import { renderCanvasOffscreen } from '@/lib/renderCanvasOffscreen'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { Header } from '@/components/ui/header'
import { EditorLoading } from '@/components/editor/editor-loading'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/shadcn/dialog'
import { Label } from '@/components/ui/shadcn/label'
import { Button } from '@/components/ui/shadcn/button'
import { Textarea } from '@/components/ui/shadcn/textarea'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { WorkspaceEditor } from '../../components/workspace/workspace-editor/workspace-editor'
import { calculateCanvasSize, loadImageDimensionsFromUrl } from '@/lib/editor'
import { presetAspectRatioForSize, toPresetAspectRatio } from '@backend/lib/util/aspectRatio'
import { WorkspacePreviewModal } from '../../components/workspace/workspace-preview/workspace-preview-modal'
import { WorkspaceLayoutPost } from '@/components/workspace/workspace-layout/workspace-layout-post'
import { WorkspaceMediaModal } from '@/components/workspace/workspace-media/workspace-media-modal'
import { VideoSlideshowDialog } from '@/components/workspace/workspace-media/video-slideshow-dialog'
import { VideoThumbnailPicker } from '@/components/workspace/workspace-media/video-thumbnail-picker'
import { WorkspaceHeader } from '@/components/workspace/workspace-header/workspace-header'
import { WorkspaceSettingsModal } from '@/components/workspace/workspace-settings-modal'
import { getWorkspaceMediaType } from '@/components/workspace/workspace-media/util'
import { WorkspaceChat } from '@/components/workspace/workspace-chat/workspace-chat'
import { useChatStore } from '@/stores/chatStore'
import { useSidebarStore } from '@/stores/sidebarStore'
import { useIsAbove1024, useMobile } from '@/hooks/useMediaQuery'
import { useTheme } from '@/context/themeContext'
import env from '@/config/env'
import {
  type RemoteFileWithUrl,
  type FileWithUrl,
  PLACEHOLDER_ASSET_KEY,
  PLACEHOLDER_ASSET_URL,
} from '@backend/lib/db/typeRoots/fileWithUrl'
import { isPublishablePlatform, type PlatformType } from '@backend/lib/db/schema/platform'
import { type ConnectedAccount } from '@backend/routes/stream/workspace-chat'
import { buildPostContentFromWorkspace } from '@backend/lib/platforms/workspaceToPostContent'
import { type CaptionRating } from '@backend/lib/ai/tools/captionRating'
import { type WorkspaceCaptions, type WorkspaceTitles } from '@backend/lib/db/schema/workspace'
import {
  type WorkspaceMedia,
  type ThumbnailCanvas,
  markAiEdited,
  markAiGenerated,
} from '@backend/lib/db/typeRoots/workspaceMedia'
import { cn, uploadToS3 } from '@/lib/utils'
import { useWorkspacePostStatus, type UpdateScheduledContentFn } from '@/lib/workspace-post-status'
import { useGeneratingImagesForWorkspace } from '@/stores/generatingImagesStore'
import { GeneratingImage } from '@/components/workspace/workspace-media/generating-media-preview'
import type { CreatorInfo } from '@backend/lib/platforms/tiktok'
import {
  PLATFORMS_WITH_AI_DISCLOSURE,
  AI_DISCLOSURE_OFF,
  aiDisclosureFromLegacyLevel,
  defaultAiDisclosure,
  defaultAiLabelText,
  detectAiProvenance,
  resolveAiProvenanceClaim,
  type AiDisclosureChoice,
  resolveAiDisclosurePlan,
  resolveAiInvolvement,
  type AiDisclosurePlatform,
} from '@backend/lib/platforms/aiDisclosure'
import { Sparkles, XIcon } from 'lucide-react'

export type WorkspaceWithChatHistories = RouterOutput['workspace']['get']
export type Role = 'static' | 'image' | 'logo'

type WorkspaceMode =
  | { type: 'media-manager' }
  | {
      type: 'editor'
      mediaIndex: number
      autoOpenDialog?: boolean
      thumbnailForVideoIndex?: number
    }

/**
 * Curated visual-direction chips. Each is a real, specific reference (a
 * brand, era, designer, magazine, or print tradition) — Impeccable's named-
 * reference rule says specificity beats vague vibe categories ("editorial",
 * "minimal"). Tapping a chip pre-fills the Direction textarea so the LLM
 * adopts it as the design's aestheticReference. Easy to edit later.
 */
const STYLE_CHIPS = [
  'Warhol screenprint',
  'Aesop product minimalism',
  'Wired magazine cover',
  'Brutalist swiss',
  'Risograph print',
  'Bauhaus poster',
  'Pirelli 1970s',
  'Liquid Death acid maximalism',
  'Mid-century editorial',
  'Klim Type specimen',
] as const

export function WorkspacePage() {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const contentContainerRef = useRef<HTMLDivElement>(null)

  // hooks
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { id: rawId } = useParams()
  const id = rawId ? parseInt(rawId, 10) : NaN
  const campaignId = searchParams.get('campaignId')
  const { theme } = useTheme()
  const { isOpen: isChatOpen, openChat, closeChat } = useChatStore()
  const { closeSidebar } = useSidebarStore()
  const isMobile = useMobile()
  const chatBesideEditor = useIsAbove1024()

  // state

  const [isWorkspaceGenerating, setIsWorkspaceGenerating] = useState(false)
  const [promptContext, setPromptContext] = useState<WorkspaceCaptions>()
  const [platformTitles, setPlatformTitles] = useState<WorkspaceTitles>({})
  const pendingTitlesRef = useRef<WorkspaceTitles | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const saveCaptionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingPromptContextRef = useRef<WorkspaceCaptions | null>(null)

  // True while the chat agent is streaming captions into the editor. Gates the
  // workspace hydration effect: a workspace.get refetch mid-stream (window
  // refocus is enough to trigger one) would otherwise overwrite the streamed
  // captions with the stale DB state.
  const agentEditingRef = useRef(false)

  const [workspaceMedia, setWorkspaceMedia] = useState<WorkspaceMedia[]>([])
  const workspaceMediaRef = useRef<WorkspaceMedia[]>([])
  workspaceMediaRef.current = workspaceMedia
  const originalMediaBeforeEditRef = useRef<WorkspaceMedia[] | null>(null)
  const [localGeneratingImages, setLocalGeneratingImages] = useState<GeneratingImage[]>([])

  // Get generating images from the global store (from home-page)
  const storeGeneratingImages = useGeneratingImagesForWorkspace(isNaN(id) ? 0 : id)

  // Combine local (from workspace chat) and store (from home-page) generating images
  const generatingImages = [...localGeneratingImages, ...storeGeneratingImages]

  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>({ type: 'media-manager' })
  const workspaceModeRef = useRef(workspaceMode)
  workspaceModeRef.current = workspaceMode
  const [isAddMediaModalOpen, setIsAddMediaModalOpen] = useState(false)
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false)
  const [isThumbnailPickerOpen, setIsThumbnailPickerOpen] = useState(false)
  const [isAutoDesignDialogOpen, setIsAutoDesignDialogOpen] = useState(false)
  // LLM-suggested style references, generated per caption + persona when the
  // dialog opens. `null` until the first call returns; falls back to
  // STYLE_CHIPS while loading or on error.
  const [suggestedReferences, setSuggestedReferences] = useState<string[] | null>(null)
  const [isLoadingReferences, setIsLoadingReferences] = useState(false)
  const [autoDesignAspectRatio, setAutoDesignAspectRatio] = useState<
    '1:1' | '4:5' | '16:9' | '9:16'
  >('1:1')
  // What to generate: one design, several options to choose from, or a
  // cohesive multi-slide carousel. `count` applies to 'options' only.
  const [autoDesignFormat, setAutoDesignFormat] = useState<'single' | 'options' | 'carousel'>(
    'single',
  )
  const [autoDesignCount, setAutoDesignCount] = useState(3)
  const [isAutoDesignGenerating, setIsAutoDesignGenerating] = useState(false)
  const [autoDesignTarget, setAutoDesignTarget] = useState<{
    media: Extract<WorkspaceMedia, { type: 'canvas' }>
    index: number
  } | null>(null)
  const [modifyPrompt, setModifyPrompt] = useState('')
  const [autoDesignPrompt, setAutoDesignPrompt] = useState('')
  const [autoDesignReferenceImage, setAutoDesignReferenceImage] = useState<string | null>(null)
  const [autoDesignUseRefColors, setAutoDesignUseRefColors] = useState(false)
  const [autoDesignRefFidelity, setAutoDesignRefFidelity] = useState(50)
  const autoDesignReferenceInputRef = useRef<HTMLInputElement>(null)

  const [activePlatforms, setActivePlatforms] = useState<PlatformType[]>(['linkedin', 'instagram'])

  const [selectedCaptionPlatform, setSelectedCaptionPlatform] = useState<PlatformType>('linkedin')
  const [postAsCarousel, setPostAsCarousel] = useState(false)
  const [publishAsDraft, setPublishAsDraft] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [settingsConfirmMode, setSettingsConfirmMode] = useState(false)
  const [settingsConfirmLabel, setSettingsConfirmLabel] = useState('Confirm & Publish')
  const settingsConfirmCallbackRef = useRef<(() => void) | null>(null)
  const [postRatings, setPostRatings] = useState<Partial<Record<PlatformType, CaptionRating>>>({})
  const [isRatingLoading, setIsRatingLoading] = useState(false)
  const ratingAnalyzeRef = useRef<(() => void) | null>(null)
  // Drift re-sync: implemented by the preview modal (owns the projection
  // machinery), triggered from the header badge's "Update content" button.
  const updateScheduledContentRef = useRef<UpdateScheduledContentFn | null>(null)

  // YouTube settings state
  const [youtubePrivacy, setYoutubePrivacy] = useState<'public' | 'unlisted' | 'private'>('public')
  const [youtubePostAsShort, setYoutubePostAsShort] = useState(false)
  const [youtubeCategory, setYoutubeCategory] = useState<string>('')

  // Instagram settings state
  const [instagramPostAsStory, setInstagramPostAsStory] = useState(false)
  const [instagramShareToFeed, setInstagramShareToFeed] = useState(true)
  const [instagramDisableComments, setInstagramDisableComments] = useState(false)
  const [instagramLocationId, setInstagramLocationId] = useState('')

  // LinkedIn settings state
  const [linkedinVisibility, setLinkedinVisibility] = useState<'PUBLIC' | 'CONNECTIONS'>('PUBLIC')
  const [linkedinDisableReshare, setLinkedinDisableReshare] = useState(false)
  const [linkedinCaptionsUrl, setLinkedinCaptionsUrl] = useState('')
  // S3 key tracked alongside the URL so we can delete the file on
  // replace/remove. Both are persisted to platformSettings.
  const [linkedinCaptionsKey, setLinkedinCaptionsKey] = useState('')

  // Facebook settings state
  const [facebookPostAsDraft, setFacebookPostAsDraft] = useState(false)
  const [facebookPostAsStory, setFacebookPostAsStory] = useState(false)
  const [facebookPostAsReel, setFacebookPostAsReel] = useState(false)
  const [facebookPlaceId, setFacebookPlaceId] = useState('')
  const [facebookFeedTargetingJson, setFacebookFeedTargetingJson] = useState('')
  const [facebookTargetingJson, setFacebookTargetingJson] = useState('')
  const [facebookVideoTitle, setFacebookVideoTitle] = useState('')
  const [facebookContentCategory, setFacebookContentCategory] = useState('')
  // TikTok settings state
  const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState<CreatorInfo | null>(null)
  const [tiktokPrivacyLevel, setTiktokPrivacyLevel] = useState<string>('')
  const [tiktokAllowComments, setTiktokAllowComments] = useState(false)
  const [tiktokAllowDuets, setTiktokAllowDuets] = useState(false)
  const [tiktokAllowStitches, setTiktokAllowStitches] = useState(false)
  const [tiktokCommercialContent, setTiktokCommercialContent] = useState(false)
  const [tiktokYourBrand, setTiktokYourBrand] = useState(false)
  const [tiktokBrandedContent, setTiktokBrandedContent] = useState(false)

  // AI-disclosure flags. Defaulted from media provenance once per workspace
  // load; user can freely override after that. Re-deriving on every media
  // change would silently overwrite a user's manual untick, so we gate on a
  // ref that flips after the first hydration.
  /** One AI-disclosure decision for the whole post; fanned out to each
   *  platform's own fields at publish time. See `platforms/aiDisclosure.ts`. */
  const [aiDisclosure, setAiDisclosure] = useState<AiDisclosureChoice>(AI_DISCLOSURE_OFF)
  /** True when the default came from media provenance rather than the user, so
   *  the UI can say so instead of looking like a setting they forgot. */
  const [aiLabelAutoDetected, setAiLabelAutoDetected] = useState(false)

  /**
   * What our IPTC marker may claim about this post's media, or null to claim
   * nothing. Sent to every platform that re-encodes on the publish path, because
   * the re-encode strips the marker and the site restating it cannot recover what
   * it said. Null is the load-bearing case: it is what stops a plain photograph
   * being stamped as AI-generated just because Instagram cropped it.
   */
  const aiProvenanceClaim = useMemo(
    () => resolveAiProvenanceClaim(workspaceMedia),
    [workspaceMedia],
  )

  /** What we found in the media. Shown in the settings dialog. */
  const aiDetection = useMemo(() => detectAiProvenance(workspaceMedia), [workspaceMedia])

  /**
   * The last label text we derived ourselves. Lets the resync below tell an
   * untouched default from wording the user typed, without a separate "is
   * custom" flag that would have to be persisted and kept in step.
   */
  const derivedAiLabelTextRef = useRef<string | null>(null)

  /**
   * Re-derive the label text when the media's provenance changes.
   *
   * Without this the text was seeded once on hydration and never revisited, so
   * the order of operations decided the wording: enable the label on a plain
   * photo, then AI-edit it, and the pill still read "AI generated". The user
   * should not have to sequence their work to get an accurate label.
   *
   * Only overwrites text we derived. Anything the user typed, or a saved brand
   * style, wins and is left alone.
   */
  useEffect(() => {
    if (!aiDisclosureHydratedRef.current) return
    const next = defaultAiLabelText(workspaceMedia)
    setAiDisclosure((current) => {
      const isOurs =
        derivedAiLabelTextRef.current !== null &&
        current.labelStyle.text === derivedAiLabelTextRef.current
      if (!isOurs || current.labelStyle.text === next) return current
      derivedAiLabelTextRef.current = next
      return { ...current, labelStyle: { ...current.labelStyle, text: next } }
    })
  }, [workspaceMedia])

  /** A baked label needs an image. Canvases flatten to one, videos and
   *  documents can't carry one. */
  const aiLabelHasImageMedia = useMemo(
    () => workspaceMedia.some((m) => m.type === 'image' || m.type === 'canvas'),
    [workspaceMedia],
  )

  /**
   * What the chosen switches actually do on each platform. Derived from the same
   * resolver the publish path calls, so the settings modal's explanation and
   * the request we send can't disagree.
   */
  const aiDisclosurePlans = useMemo(
    () =>
      Object.fromEntries(
        PLATFORMS_WITH_AI_DISCLOSURE.map((platform) => [
          platform,
          resolveAiDisclosurePlan(aiDisclosure, platform, aiLabelHasImageMedia),
        ]),
      ) as Record<AiDisclosurePlatform, ReturnType<typeof resolveAiDisclosurePlan>>,
    [aiDisclosure, aiLabelHasImageMedia],
  )
  const aiDisclosureHydratedRef = useRef(false)

  // TikTok publish flow (Direct Post vs Inbox draft).
  const [tiktokPostMode, setTiktokPostMode] = useState<'DIRECT_POST' | 'MEDIA_UPLOAD'>(
    'DIRECT_POST',
  )
  // Auto-add background music for TikTok photo carousels.
  const [tiktokAutoAddMusic, setTiktokAutoAddMusic] = useState(false)

  // YouTube extended metadata.
  const [youtubeDefaultLanguage, setYoutubeDefaultLanguage] = useState('')
  const [youtubeDefaultAudioLanguage, setYoutubeDefaultAudioLanguage] = useState('')
  const [youtubeNotifySubscribers, setYoutubeNotifySubscribers] = useState(true)
  const [youtubeLicense, setYoutubeLicense] = useState<'youtube' | 'creativeCommon'>('youtube')
  const [youtubeEmbeddable, setYoutubeEmbeddable] = useState(true)
  const [youtubePublicStatsViewable, setYoutubePublicStatsViewable] = useState(true)
  const [youtubeLocalizationsJson, setYoutubeLocalizationsJson] = useState('')
  const [youtubeCaptionsUrl, setYoutubeCaptionsUrl] = useState('')
  const [youtubeCaptionsKey, setYoutubeCaptionsKey] = useState('')
  const [youtubeCaptionsLanguage, setYoutubeCaptionsLanguage] = useState('')
  const [youtubePlaylistId, setYoutubePlaylistId] = useState('')

  // trpc

  const {
    data: workspace,
    isLoading: isWorkspaceLoading,
    isError: isWorkspaceError,
  } = trpc.workspace.get.useQuery({ id }, { enabled: !campaignId && !isNaN(id) })

  const { data: campaignPost } = trpc.campaign.post.get.useQuery(
    { id },
    { enabled: !!campaignId && !isNaN(id) },
  )

  // The brand's saved label style, used as the starting point for a new post's
  // appearance. Null means "use CASSI's defaults", which is also what Reset
  // restores — so the two stay distinguishable.
  const { data: currentBrand } = trpc.brand.getCurrent.useQuery()
  const brandUtils = trpc.useUtils()
  const { mutateAsync: updateBrand } = trpc.brand.update.useMutation({
    onSuccess: () => brandUtils.brand.getCurrent.invalidate(),
  })
  const savedAiLabelStyle = currentBrand?.aiLabelStyle ?? null

  /** Set when the workspace carries its own saved disclosure. The brand's
   *  default is a starting point for new posts, never an override of one. */
  const workspaceHasOwnDisclosureRef = useRef(false)
  const brandStyleAppliedRef = useRef(false)

  /**
   * Applies the brand's saved label style once the brand query resolves.
   *
   * The workspace hydration reads `savedAiLabelStyle` too, but that effect is
   * gated behind a one-shot ref and does not depend on the brand query. On a cold
   * load the brand usually resolves second, so the saved style was read as null
   * and silently ignored — while the panel still claimed new posts start from it.
   */
  useEffect(() => {
    if (!savedAiLabelStyle || brandStyleAppliedRef.current) return
    if (!aiDisclosureHydratedRef.current || !platformSettingsHydratedRef.current) return
    brandStyleAppliedRef.current = true
    if (workspaceHasOwnDisclosureRef.current) return
    setAiDisclosure((current) => ({ ...current, labelStyle: savedAiLabelStyle }))
  }, [savedAiLabelStyle, workspace])

  const { mutateAsync: createWorkspace } = trpc.workspace.create.useMutation()
  const { mutateAsync: updateWorkspace } = trpc.workspace.update.useMutation({
    // Every save can flip a scheduled post into "drifted" — refetch so the
    // header's Update-content button appears without a page reload.
    onSuccess: () => utils.workspace.postStatus.invalidate(),
  })
  const { mediaLocked, isCaptionLocked } = useWorkspacePostStatus(workspace?.id)
  const { mutateAsync: getUploadUrl } = trpc.asset.getUploadUrl.useMutation()
  const { mutateAsync: generateCanvas } = trpc.socialMediaPost.generateCanvas.useMutation()
  const { mutateAsync: suggestDesignReferences } =
    trpc.socialMediaPost.suggestDesignReferences.useMutation()
  const { mutateAsync: createPlatformPost } = trpc.platform.post.useMutation()
  const { mutateAsync: createScheduledPost } = trpc.scheduledPost.create.useMutation()
  const { mutateAsync: updateScheduledPost } = trpc.scheduledPost.update.useMutation()
  const { mutateAsync: deleteScheduledPost } = trpc.scheduledPost.delete.useMutation()
  const { mutateAsync: copyAsset } = trpc.asset.copyAsset.useMutation()
  const utils = trpc.useUtils()

  // Track which workspace ID we've migrated (to avoid re-running on same workspace)
  const migratedWorkspaceIdRef = useRef<number | null>(null)
  // Track the last saved activePlatforms to avoid saving on initial load
  const lastSavedActivePlatformsRef = useRef<string | null>(null)
  // Hydration gate: only populate per-platform state from DB once per workspace.
  // Subsequent workspace refetches (e.g. post-generation polling) shouldn't
  // clobber in-flight user edits.
  const platformSettingsHydratedRef = useRef(false)
  // Debounce + skip-initial guard for the persistence write-back.
  const lastSavedPlatformSettingsRef = useRef<string | null>(null)

  const { data: linkedinData } = trpc.platform.isAuthenticated.useQuery({ platform: 'linkedin' })
  const { data: instagramData } = trpc.platform.isAuthenticated.useQuery({ platform: 'instagram' })
  const { data: tiktokData } = trpc.platform.isAuthenticated.useQuery({ platform: 'tiktok' })
  const { data: youtubeData } = trpc.platform.isAuthenticated.useQuery({ platform: 'youtube' })
  const { data: facebookData } = trpc.platform.isAuthenticated.useQuery({ platform: 'facebook' })

  const connectedAccounts = useMemo<ConnectedAccount[]>(() => {
    const accounts: ConnectedAccount[] = []
    const platformData = [
      { platform: 'linkedin' as const, data: linkedinData },
      { platform: 'instagram' as const, data: instagramData },
      { platform: 'tiktok' as const, data: tiktokData },
      { platform: 'youtube' as const, data: youtubeData },
      { platform: 'facebook' as const, data: facebookData },
    ]
    for (const { platform, data } of platformData) {
      if (data?.isAuthenticated) {
        for (const account of data.accounts) {
          accounts.push({ platform, accountId: account.id, displayName: account.displayName })
        }
      }
    }
    return accounts
  }, [linkedinData, instagramData, tiktokData, youtubeData, facebookData])

  // Fetch TikTok creator info when TikTok is authenticated (for settings & posting)
  const tiktokAccountId = tiktokData?.isAuthenticated ? tiktokData.accounts[0]?.id : undefined
  const {
    data: fetchedTiktokCreatorInfo,
    isLoading: isTiktokCreatorInfoLoading,
    error: tiktokCreatorInfoError,
  } = trpc.platform.creatorInfo.useQuery(
    { platform: 'tiktok', accountId: tiktokAccountId ?? '' },
    { enabled: !!tiktokAccountId, retry: false },
  )

  useEffect(() => {
    if (fetchedTiktokCreatorInfo) {
      // Store creator info only. Do NOT seed the interaction toggles from it:
      // TikTok's Content Sharing Guidelines require that "users must manually
      // turn on these interaction settings and none should be checked by
      // default". The `*_disabled` flags say which boxes to grey out, never
      // which to pre-check — mirroring the creator's account settings here is
      // what failed the Direct Post audit.
      setTiktokCreatorInfo(fetchedTiktokCreatorInfo)
    }
  }, [fetchedTiktokCreatorInfo])

  // effects

  // On mobile: close chat and sidebar for better UX
  // On desktop: open chat when navigating to a new workspace
  useEffect(() => {
    if (isMobile) {
      closeChat()
      closeSidebar()
    } else {
      openChat()
    }
  }, [id, isMobile, openChat, closeChat, closeSidebar])

  // NOTE:  temporary until we have a theme across all pages
  useEffect(() => {
    document.body.classList.add('workspace-page')

    if (theme === 'dark') {
      document.body.classList.add('dark')
    } else {
      document.body.classList.remove('dark')
    }

    return () => {
      document.body.classList.remove('workspace-page', 'dark')
    }
  }, [theme])

  // initialize workspace
  useEffect(() => {
    if (campaignId && campaignPost) {
      createWorkspace({
        title: campaignPost.campaign.title,
        status: 'success',
        media: campaignPost.files.map((file) => ({
          type: getWorkspaceMediaType(file.file.type),
          file,
        })),
        captions: {
          instagram: campaignPost.caption ?? '',
          linkedin: campaignPost.caption ?? '',
        },
        activePlatforms,
      }).then((newWorkspace) => {
        navigate(`/workspace/${newWorkspace.id}`)
      })
    } else {
      if (isWorkspaceLoading) return

      if (isWorkspaceError) {
        navigate('/')
        return
      }

      if (workspace) {
        // In editor mode, skip server syncs to protect local canvas state
        // (unsaved node changes, image→canvas conversions live only in local state).
        // In media-manager mode, allow server updates to flow through
        // so post generation results (captions, media) are reflected.
        if (workspaceModeRef.current.type === 'editor') return

        // While the agent streams captions, the local editor state is ahead of
        // the DB — don't let a refetch overwrite it. Media and settings below
        // still hydrate normally.
        if (!agentEditingRef.current) {
          setPromptContext(workspace.captions)
          setPlatformTitles(workspace.titles ?? {})
          setActivePlatforms(workspace.activePlatforms)
          setSelectedCaptionPlatform((prev) =>
            workspace.activePlatforms.includes(prev)
              ? prev
              : (workspace.activePlatforms[0] ?? prev),
          )
        }
        const media = workspace.media
        setWorkspaceMedia(media)
        setIsWorkspaceGenerating(workspace.status === 'generating')

        // Seed AI-disclosure defaults from media provenance on first hydration only.
        if (!aiDisclosureHydratedRef.current) {
          const seeded = defaultAiDisclosure(media)
          // A saved brand style is the user's own default; CASSI's tuned values
          // are the fallback, not an override of their preference. When there is
          // no saved style, seed the text from provenance so an AI edit of a real
          // photograph doesn't ship a pill claiming the whole thing was generated.
          if (savedAiLabelStyle) {
            setAiDisclosure({ ...seeded, labelStyle: savedAiLabelStyle })
          } else {
            const derivedText = defaultAiLabelText(media)
            derivedAiLabelTextRef.current = derivedText
            setAiDisclosure({
              ...seeded,
              labelStyle: { ...seeded.labelStyle, text: derivedText },
            })
          }
          setAiLabelAutoDetected(seeded.nativeLabel || seeded.visibleLabel)
          aiDisclosureHydratedRef.current = true
        }

        // Hydrate per-platform settings from the persisted platformSettings
        // JSONB on first workspace load. Gate via ref so subsequent refetches
        // don't clobber the user's in-flight edits.
        if (!platformSettingsHydratedRef.current) {
          const ps = workspace.platformSettings ?? {}
          if (ps.instagram) {
            if (ps.instagram.postAsStory !== undefined)
              setInstagramPostAsStory(ps.instagram.postAsStory)
            if (ps.instagram.shareToFeed !== undefined)
              setInstagramShareToFeed(ps.instagram.shareToFeed)
            if (ps.instagram.disableComments !== undefined)
              setInstagramDisableComments(ps.instagram.disableComments)
            if (ps.instagram.locationId !== undefined)
              setInstagramLocationId(ps.instagram.locationId)
          }
          if (ps.linkedin) {
            if (ps.linkedin.visibility !== undefined) setLinkedinVisibility(ps.linkedin.visibility)
            if (ps.linkedin.disableReshare !== undefined)
              setLinkedinDisableReshare(ps.linkedin.disableReshare)
            if (ps.linkedin.publishAsDraft !== undefined)
              setPublishAsDraft(ps.linkedin.publishAsDraft)
            if (ps.linkedin.captionsUrl !== undefined)
              setLinkedinCaptionsUrl(ps.linkedin.captionsUrl)
            if (ps.linkedin.captionsKey !== undefined)
              setLinkedinCaptionsKey(ps.linkedin.captionsKey)
          }
          // `postAsCarousel`: persisted value wins; new workspaces default
          // to true so a multi-image upload becomes a carousel automatically.
          // The modal's clamp keeps this consistent — it only force-resets
          // to false when image count transitions from ≥2 down to <2, so a
          // `true` default on a brand-new empty workspace doesn't get
          // clobbered before the user has had a chance to add media.
          setPostAsCarousel(ps.linkedin?.postAsCarousel ?? true)
          if (ps.tiktok) {
            if (ps.tiktok.privacyLevel !== undefined) setTiktokPrivacyLevel(ps.tiktok.privacyLevel)
            if (ps.tiktok.allowComments !== undefined)
              setTiktokAllowComments(ps.tiktok.allowComments)
            if (ps.tiktok.allowDuets !== undefined) setTiktokAllowDuets(ps.tiktok.allowDuets)
            if (ps.tiktok.allowStitches !== undefined)
              setTiktokAllowStitches(ps.tiktok.allowStitches)
            if (ps.tiktok.commercialContent !== undefined)
              setTiktokCommercialContent(ps.tiktok.commercialContent)
            if (ps.tiktok.yourBrand !== undefined) setTiktokYourBrand(ps.tiktok.yourBrand)
            if (ps.tiktok.brandedContent !== undefined)
              setTiktokBrandedContent(ps.tiktok.brandedContent)
            if (ps.tiktok.postMode !== undefined) setTiktokPostMode(ps.tiktok.postMode)
            if (ps.tiktok.autoAddMusic !== undefined) setTiktokAutoAddMusic(ps.tiktok.autoAddMusic)
          }
          if (ps.youtube) {
            if (ps.youtube.privacy !== undefined) setYoutubePrivacy(ps.youtube.privacy)
            if (ps.youtube.postAsShort !== undefined) setYoutubePostAsShort(ps.youtube.postAsShort)
            if (ps.youtube.category !== undefined) setYoutubeCategory(ps.youtube.category)
            if (ps.youtube.defaultLanguage !== undefined)
              setYoutubeDefaultLanguage(ps.youtube.defaultLanguage)
            if (ps.youtube.defaultAudioLanguage !== undefined)
              setYoutubeDefaultAudioLanguage(ps.youtube.defaultAudioLanguage)
            if (ps.youtube.notifySubscribers !== undefined)
              setYoutubeNotifySubscribers(ps.youtube.notifySubscribers)
            if (ps.youtube.license !== undefined) setYoutubeLicense(ps.youtube.license)
            if (ps.youtube.embeddable !== undefined) setYoutubeEmbeddable(ps.youtube.embeddable)
            if (ps.youtube.publicStatsViewable !== undefined)
              setYoutubePublicStatsViewable(ps.youtube.publicStatsViewable)
            if (ps.youtube.localizationsJson !== undefined)
              setYoutubeLocalizationsJson(ps.youtube.localizationsJson)
            if (ps.youtube.captionsUrl !== undefined) setYoutubeCaptionsUrl(ps.youtube.captionsUrl)
            if (ps.youtube.captionsKey !== undefined) setYoutubeCaptionsKey(ps.youtube.captionsKey)
            if (ps.youtube.captionsLanguage !== undefined)
              setYoutubeCaptionsLanguage(ps.youtube.captionsLanguage)
            if (ps.youtube.playlistId !== undefined) setYoutubePlaylistId(ps.youtube.playlistId)
          }
          if (ps.facebook) {
            if (ps.facebook.postAsDraft !== undefined)
              setFacebookPostAsDraft(ps.facebook.postAsDraft)
            if (ps.facebook.postAsStory !== undefined)
              setFacebookPostAsStory(ps.facebook.postAsStory)
            if (ps.facebook.postAsReel !== undefined) setFacebookPostAsReel(ps.facebook.postAsReel)
            if (ps.facebook.placeId !== undefined) setFacebookPlaceId(ps.facebook.placeId)
            if (ps.facebook.feedTargetingJson !== undefined)
              setFacebookFeedTargetingJson(ps.facebook.feedTargetingJson)
            if (ps.facebook.targetingJson !== undefined)
              setFacebookTargetingJson(ps.facebook.targetingJson)
            if (ps.facebook.videoTitle !== undefined) setFacebookVideoTitle(ps.facebook.videoTitle)
            if (ps.facebook.contentCategory !== undefined)
              setFacebookContentCategory(ps.facebook.contentCategory)
          }

          // AI disclosure: a persisted value wins over the media-derived
          // default, so a user's choice survives a reload. We now write the
          // same decision to every platform, but workspaces saved before the
          // toggle was unified can hold five independent values — OR them so a
          // disclosure that was previously on for any platform stays on. A
          // false negative here would silently drop a compliance label.
          if (ps.aiDisclosure !== undefined) {
            setAiDisclosure(ps.aiDisclosure)
            setAiLabelAutoDetected(false)
            workspaceHasOwnDisclosureRef.current = true
          } else if (ps.aiDisclosureLevel !== undefined) {
            // Saved under the single-axis level model.
            setAiDisclosure(aiDisclosureFromLegacyLevel(ps.aiDisclosureLevel))
            setAiLabelAutoDetected(false)
          } else {
            // Workspaces saved before the level model only carry the old
            // per-platform booleans, and those could disagree with each other.
            // OR them: a false negative would silently drop a compliance
            // label. They map to `aiGenerated`, never to the stronger level,
            // since nothing in the old data recorded that judgement.
            const legacy = [
              ps.instagram?.isAiGenerated,
              ps.facebook?.isAiGenerated,
              ps.linkedin?.isAiGenerated,
              ps.tiktok?.isAigc,
              ps.youtube?.containsSyntheticMedia,
            ].filter((value) => value !== undefined)
            if (legacy.length > 0) {
              setAiDisclosure(
                aiDisclosureFromLegacyLevel(legacy.some(Boolean) ? 'aiGenerated' : 'off'),
              )
              setAiLabelAutoDetected(false)
            }
          }

          platformSettingsHydratedRef.current = true
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    navigate,
    isWorkspaceLoading,
    isWorkspaceError,
    workspace,
    campaignId,
    campaignPost,
    createWorkspace,
  ])

  // When navigating between workspaces, reset every per-platform field to
  // its default. The hydration effect then re-populates from the new
  // workspace's persisted platformSettings on load. Without this reset, the
  // OLD workspace's values would leak into the new workspace for any field
  // that the new workspace doesn't have persisted yet (e.g. a fresh
  // workspace where the user never touched any setting).
  useEffect(() => {
    // Re-arm the hydration gates so the new workspace's persisted values
    // overwrite these defaults once `workspace` arrives.
    platformSettingsHydratedRef.current = false
    lastSavedPlatformSettingsRef.current = null
    aiDisclosureHydratedRef.current = false

    setInstagramPostAsStory(false)
    setInstagramShareToFeed(true)
    setInstagramDisableComments(false)
    setInstagramLocationId('')

    setLinkedinVisibility('PUBLIC')
    setLinkedinDisableReshare(false)
    setPublishAsDraft(false)
    setPostAsCarousel(false)
    setLinkedinCaptionsUrl('')
    setLinkedinCaptionsKey('')

    // One AI-disclosure decision for every platform, so one reset.
    setAiDisclosure(AI_DISCLOSURE_OFF)
    setAiLabelAutoDetected(false)

    setTiktokPrivacyLevel('')
    setTiktokAllowComments(false)
    setTiktokAllowDuets(false)
    setTiktokAllowStitches(false)
    setTiktokCommercialContent(false)
    setTiktokYourBrand(false)
    setTiktokBrandedContent(false)
    setTiktokPostMode('DIRECT_POST')
    setTiktokAutoAddMusic(false)

    setYoutubePrivacy('public')
    setYoutubePostAsShort(false)
    setYoutubeCategory('')
    setYoutubeDefaultLanguage('')
    setYoutubeDefaultAudioLanguage('')
    setYoutubeNotifySubscribers(true)
    setYoutubeLicense('youtube')
    setYoutubeEmbeddable(true)
    setYoutubePublicStatsViewable(true)
    setYoutubeLocalizationsJson('')
    setYoutubeCaptionsUrl('')
    setYoutubeCaptionsKey('')
    setYoutubeCaptionsLanguage('')
    setYoutubePlaylistId('')

    setFacebookPostAsDraft(false)
    setFacebookPostAsStory(false)
    setFacebookPostAsReel(false)
    setFacebookPlaceId('')
    setFacebookFeedTargetingJson('')
    setFacebookTargetingJson('')
    setFacebookVideoTitle('')
    setFacebookContentCategory('')
  }, [id])

  // Save activePlatforms to DB when they change
  useEffect(() => {
    if (!workspace) return
    const key = JSON.stringify(activePlatforms)
    // Skip the initial hydration from DB
    if (lastSavedActivePlatformsRef.current === null) {
      lastSavedActivePlatformsRef.current = key
      return
    }
    // Skip if unchanged
    if (lastSavedActivePlatformsRef.current === key) return
    lastSavedActivePlatformsRef.current = key

    updateWorkspace({ id: workspace.id, data: { activePlatforms } })
  }, [activePlatforms, workspace, updateWorkspace])

  // Debounced persistence of all per-platform settings. Builds the full
  // platformSettings object from current state, compares against the last
  // saved JSON, and writes back after a 600ms quiet window so that rapid
  // edits (typing in the Location field, JSON textareas, etc.) collapse
  // into one PATCH. The hydration gate guarantees this effect doesn't fire
  // a write on initial mount from the freshly-hydrated state.
  useEffect(() => {
    if (!workspace || !platformSettingsHydratedRef.current) return

    const current = {
      aiDisclosure,
      instagram: {
        postAsStory: instagramPostAsStory,
        shareToFeed: instagramShareToFeed,
        disableComments: instagramDisableComments,
        locationId: instagramLocationId,
        isAiGenerated: aiDisclosurePlans.instagram.nativeFlag,
      },
      linkedin: {
        visibility: linkedinVisibility,
        disableReshare: linkedinDisableReshare,
        publishAsDraft,
        postAsCarousel,
        captionsUrl: linkedinCaptionsUrl,
        captionsKey: linkedinCaptionsKey,
        // LinkedIn's own plan, not Instagram's. This is always false — LinkedIn
        // has no native disclosure field — and it is only read by the legacy
        // hydration fallback, which ORs these per-platform booleans. Writing
        // another platform's value here made that fallback report a LinkedIn
        // disclosure that never existed.
        isAiGenerated: aiDisclosurePlans.linkedin.nativeFlag,
      },
      tiktok: {
        privacyLevel: tiktokPrivacyLevel,
        allowComments: tiktokAllowComments,
        allowDuets: tiktokAllowDuets,
        allowStitches: tiktokAllowStitches,
        commercialContent: tiktokCommercialContent,
        yourBrand: tiktokYourBrand,
        brandedContent: tiktokBrandedContent,
        isAigc: aiDisclosurePlans.tiktok.nativeFlag,
        postMode: tiktokPostMode,
        autoAddMusic: tiktokAutoAddMusic,
      },
      youtube: {
        privacy: youtubePrivacy,
        postAsShort: youtubePostAsShort,
        category: youtubeCategory,
        containsSyntheticMedia: aiDisclosurePlans.youtube.nativeFlag,
        defaultLanguage: youtubeDefaultLanguage,
        defaultAudioLanguage: youtubeDefaultAudioLanguage,
        notifySubscribers: youtubeNotifySubscribers,
        license: youtubeLicense,
        embeddable: youtubeEmbeddable,
        publicStatsViewable: youtubePublicStatsViewable,
        localizationsJson: youtubeLocalizationsJson,
        captionsUrl: youtubeCaptionsUrl,
        captionsKey: youtubeCaptionsKey,
        captionsLanguage: youtubeCaptionsLanguage,
        playlistId: youtubePlaylistId,
      },
      facebook: {
        postAsDraft: facebookPostAsDraft,
        postAsStory: facebookPostAsStory,
        postAsReel: facebookPostAsReel,
        placeId: facebookPlaceId,
        feedTargetingJson: facebookFeedTargetingJson,
        targetingJson: facebookTargetingJson,
        videoTitle: facebookVideoTitle,
        contentCategory: facebookContentCategory,
        isAiGenerated: aiDisclosurePlans.facebook.nativeFlag,
      },
    }
    const key = JSON.stringify(current)
    if (lastSavedPlatformSettingsRef.current === null) {
      lastSavedPlatformSettingsRef.current = key
      return
    }
    if (lastSavedPlatformSettingsRef.current === key) return

    const workspaceId = workspace.id
    const timer = setTimeout(() => {
      lastSavedPlatformSettingsRef.current = key
      updateWorkspace({ id: workspaceId, data: { platformSettings: current } })
    }, 600)
    return () => clearTimeout(timer)
  }, [
    workspace,
    updateWorkspace,
    instagramPostAsStory,
    instagramShareToFeed,
    instagramDisableComments,
    instagramLocationId,
    linkedinVisibility,
    linkedinDisableReshare,
    publishAsDraft,
    postAsCarousel,
    linkedinCaptionsUrl,
    linkedinCaptionsKey,
    tiktokPrivacyLevel,
    tiktokAllowComments,
    tiktokAllowDuets,
    tiktokAllowStitches,
    tiktokCommercialContent,
    tiktokYourBrand,
    tiktokBrandedContent,
    aiDisclosure,
    aiDisclosurePlans,
    tiktokPostMode,
    tiktokAutoAddMusic,
    youtubePrivacy,
    youtubePostAsShort,
    youtubeCategory,
    youtubeDefaultLanguage,
    youtubeDefaultAudioLanguage,
    youtubeNotifySubscribers,
    youtubeLicense,
    youtubeEmbeddable,
    youtubePublicStatsViewable,
    youtubeLocalizationsJson,
    youtubeCaptionsUrl,
    youtubeCaptionsKey,
    youtubeCaptionsLanguage,
    youtubePlaylistId,
    facebookPostAsDraft,
    facebookPostAsStory,
    facebookPostAsReel,
    facebookPlaceId,
    facebookFeedTargetingJson,
    facebookTargetingJson,
    facebookVideoTitle,
    facebookContentCategory,
  ])

  // Recalculate textarea height when content changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [promptContext, workspaceMode, selectedCaptionPlatform, workspaceMedia])

  // Recalculate textarea height when container width changes (e.g., chat panel opens/closes)
  useEffect(() => {
    const container = contentContainerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
      }
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [workspaceMode, workspace])

  // Migrate placeholder canvas files (from database migration)
  // Runs once per workspace when loaded, checks for broken images and regenerates them
  useEffect(() => {
    if (!workspace) return
    if (migratedWorkspaceIdRef.current === workspace.id) return

    // Mark as checked immediately to prevent re-runs
    migratedWorkspaceIdRef.current = workspace.id

    let aborted = false

    const migratePlaceholderCanvases = async () => {
      // Helper to check if a canvas file needs regeneration
      const needsRegeneration = async (
        media: Extract<WorkspaceMedia, { type: 'canvas' }>,
      ): Promise<boolean> => {
        // Must have nodes to render
        if (media.nodes.length === 0) return false

        // Check for obvious placeholder indicators
        if (media.file.file.size === 0) return true
        if (!media.file.url) return true

        // Try to load the image to see if it's valid (using Image to avoid CORS issues)
        try {
          await new Promise<void>((resolve, reject) => {
            const img = new Image()
            img.crossOrigin = 'anonymous'
            img.onload = () => resolve()
            img.onerror = () => reject()
            img.src = media.file.url
          })
          return false // Image loaded successfully
        } catch {
          return true // Image failed to load
        }
      }

      // Find all canvas media that need regeneration
      const canvasMedia = workspace.media
        .filter((media) => media.type === 'canvas')
        .map((media, index) => ({ media, index }))

      const checks = await Promise.all(
        canvasMedia.map(async ({ media, index }) => ({
          index,
          needsRegen: await needsRegeneration(media),
        })),
      )

      if (aborted) return

      const placeholderIndices = checks
        .filter(({ needsRegen }) => needsRegen)
        .map(({ index }) => index)

      if (placeholderIndices.length === 0) return

      let updatedMedia = [...workspace.media]

      for (const index of placeholderIndices) {
        if (aborted) return

        const media = updatedMedia[index]
        if (media?.type !== 'canvas') continue

        try {
          const file = await renderCanvasOffscreen({
            nodes: media.nodes,
            aspectRatio: media.aspectRatio,
            aiProvenance: media,
          })

          if (aborted) return

          const { key, uploadUrl, assetUrl } = await getUploadUrl({
            type: 'workspace',
            workspaceId: workspace.id,
          })
          await uploadToS3(uploadUrl, file)

          if (aborted) return

          updatedMedia = updatedMedia.map((m, i) =>
            i === index && m.type === 'canvas'
              ? {
                  ...m,
                  file: {
                    source: 'remote' as const,
                    url: assetUrl,
                    key,
                    file: { name: file.name, type: file.type, size: file.size },
                  },
                }
              : m,
          )
        } catch (error) {
          console.error(`Error migrating placeholder canvas at index ${index}:`, error)
        }
      }

      if (aborted) return

      // Use functional update to only replace migrated items, preserving any user edits
      setWorkspaceMedia(updatedMedia)

      await updateWorkspace({
        id: workspace.id,
        data: { media: updatedMedia },
      })

      utils.workspace.get.invalidate({ id: workspace.id })
      utils.workspace.list.invalidate()
    }

    migratePlaceholderCanvases()

    return () => {
      aborted = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id])

  // handlers

  // Shared debounced save for captions and titles — both refs are flushed
  // together so a rapid caption edit followed by a title edit (or vice versa)
  // doesn't race and drop one of the two.
  const scheduleDebouncedSave = () => {
    if (saveCaptionTimeoutRef.current) {
      clearTimeout(saveCaptionTimeoutRef.current)
    }
    saveCaptionTimeoutRef.current = setTimeout(async () => {
      try {
        const data: Parameters<typeof updateWorkspace>[0]['data'] = {}
        if (pendingPromptContextRef.current) data.captions = pendingPromptContextRef.current
        if (pendingTitlesRef.current) data.titles = pendingTitlesRef.current
        if (Object.keys(data).length > 0) {
          await updateWorkspace({ id: workspace!.id, data })
          utils.workspace.list.invalidate()
        }
      } catch (error) {
        console.error('Error updating workspace text:', error)
      } finally {
        setIsSaving(false)
      }
    }, 2000)
  }

  const handleCaptionChange = (value: string, platform: PlatformType) => {
    setIsSaving(true)
    setPromptContext((prev) => {
      const newPromptContext = { ...prev, [platform]: value }
      pendingPromptContextRef.current = newPromptContext
      return newPromptContext
    })
    setActivePlatforms((prev) => (prev.includes(platform) ? prev : [...prev, platform]))
    scheduleDebouncedSave()
  }

  const handleTitleChange = (value: string, platform: PlatformType) => {
    setIsSaving(true)
    setPlatformTitles((prev) => {
      const newTitles = { ...prev, [platform]: value }
      pendingTitlesRef.current = newTitles
      return newTitles
    })
    scheduleDebouncedSave()
  }

  /**
   * Media is shared across platforms, so it locks as soon as anything is
   * published from this workspace — the workspace stays the record of what
   * went out. Guarding the setter covers upload, delete, reorder and
   * edit-to-canvas in one place. (Server-side, `asset.delete` independently
   * protects objects referenced by posts.)
   */
  const guardMediaLocked = () => {
    if (!mediaLocked) return false
    toast.error(
      'Media is locked — this workspace has published posts. Duplicate it to keep editing.',
    )
    return true
  }

  const handleSetMedia = async (media: WorkspaceMedia[], updateDb: boolean) => {
    if (!workspace) return
    if (guardMediaLocked()) return
    setWorkspaceMedia(media)

    // Clear thumbnail when the last video is removed
    const shouldClearThumbnail = workspace.thumbnail && !media.some((m) => m.type === 'video')

    if (updateDb) {
      await updateWorkspace({
        id: workspace.id,
        data: { media, ...(shouldClearThumbnail ? { thumbnail: null } : {}) },
      })
      if (shouldClearThumbnail) {
        utils.workspace.get.invalidate({ id: workspace.id })
      }
    }
  }

  const handleSetThumbnail = async (thumbnail: ThumbnailCanvas | null) => {
    if (!workspace) return
    await updateWorkspace({ id: workspace.id, data: { thumbnail } })
    utils.workspace.get.invalidate({ id: workspace.id })
  }

  /** Get the closest supported aspect ratio for a video */
  const getVideoAspectRatio = (videoUrl: string): Promise<string> =>
    new Promise((resolve) => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.src = videoUrl
      let settled = false
      const settle = (ratio: string) => {
        if (settled) return
        settled = true
        video.src = ''
        video.load()
        resolve(ratio)
      }
      video.onloadedmetadata = () =>
        settle(presetAspectRatioForSize(video.videoWidth, video.videoHeight))
      video.onerror = () => settle('16:9')
      setTimeout(() => settle('16:9'), 10_000)
    })

  /** Create a ThumbnailCanvas from an uploaded image URL */
  const createThumbnailCanvasFromImage = (
    imageUrl: string,
    key: string,
    aspectRatio: string,
  ): ThumbnailCanvas => {
    const dimensions = calculateCanvasSize(aspectRatio)
    const imageNode = createImageNode(imageUrl, dimensions)
    return {
      nodes: [imageNode],
      aspectRatio,
      file: {
        source: 'remote',
        url: imageUrl,
        key,
        file: { name: 'thumbnail.jpg', type: 'image/jpeg', size: 0 },
      },
    }
  }

  const autoDesignCaption = promptContext?.[selectedCaptionPlatform] || ''

  /** Ask the LLM for a fresh set of style references tailored to the current
   *  caption + persona. User triggers this explicitly via the suggest button —
   *  the dialog opens with static defaults so it's instant. */
  const handleSuggestReferences = async () => {
    const caption = autoDesignCaption.trim() || modifyPrompt.trim()
    if (!caption) return
    setIsLoadingReferences(true)
    try {
      const res = await suggestDesignReferences({
        caption,
        moodboardId: workspace?.moodboardId ?? undefined,
      })
      if (res.references && res.references.length >= 4) {
        setSuggestedReferences(res.references)
      }
    } catch {
      // Keep whatever was on screen
    } finally {
      setIsLoadingReferences(false)
    }
  }

  const referenceChips = suggestedReferences ?? [...STYLE_CHIPS]
  const canSuggest = autoDesignCaption.trim().length > 0 || modifyPrompt.trim().length > 0

  const blobToBase64 = (blob: Blob) =>
    new Promise<string>((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const dataUrl = reader.result as string
        resolve(dataUrl.split(',')[1]!)
      }
      reader.readAsDataURL(blob)
    })

  const fetchImageAsBase64 = async (url: string) => {
    const imageResponse = await fetch(url)
    const imageBlob = await imageResponse.blob()
    return blobToBase64(imageBlob)
  }

  const createEmptyCanvasMedia = (): Extract<WorkspaceMedia, { type: 'canvas' }> => ({
    type: 'canvas',
    nodes: [],
    aspectRatio: '1:1',
    file: remotePlaceholderFile,
  })

  const handleAutoDesignGenerate = async () => {
    if (!workspace || !autoDesignCaption.trim()) return

    setIsAutoDesignGenerating(true)
    setIsAutoDesignDialogOpen(false)

    const generatingLabel =
      autoDesignFormat === 'carousel'
        ? 'Designing carousel for caption...'
        : autoDesignFormat === 'options'
          ? `Designing ${autoDesignCount} options...`
          : 'Designing graphic for caption...'
    setLocalGeneratingImages((prev) => [...prev, { id: 'auto-design', prompt: generatingLabel }])

    try {
      const result = await generateCanvas({
        caption: autoDesignCaption,
        prompt: autoDesignPrompt.trim() || undefined,
        format: autoDesignFormat,
        count: autoDesignFormat === 'options' ? autoDesignCount : undefined,
        referenceImage: autoDesignReferenceImage ?? undefined,
        useReferenceColors: autoDesignReferenceImage ? autoDesignUseRefColors : undefined,
        referenceFidelity: autoDesignReferenceImage ? autoDesignRefFidelity : undefined,
        aspectRatio: autoDesignAspectRatio,
        workspaceId: workspace.id,
        moodboardId: workspace.moodboardId ?? undefined,
      })

      if (!result.ok) {
        toast.error('Usage limit exceeded. Please upgrade your plan.')
        return
      }

      // Render each slide offscreen, upload, and collect. A single design is one
      // slide; options/carousel return several — each becomes its own canvas item.
      const newMedia: WorkspaceMedia[] = []
      for (const [i, slide] of result.slides.entries()) {
        const file = await renderCanvasOffscreen({
          nodes: slide.nodes,
          aspectRatio: slide.aspectRatio,
          aiProvenance: markAiGenerated(),
        })
        const { key, uploadUrl, assetUrl } = await getUploadUrl({
          type: 'workspace',
          workspaceId: workspace.id,
        })
        await uploadToS3(uploadUrl, file)
        newMedia.push({
          type: 'canvas',
          nodes: slide.nodes,
          aspectRatio: slide.aspectRatio,
          file: {
            source: 'remote',
            url: assetUrl,
            key,
            file: { name: file.name, type: file.type, size: file.size },
          },
          ...markAiGenerated(),
        })

        // Debug HTML screenshot per slide (dev only).
        if (env.MODE === 'development' && slide.debugHtmlScreenshotUrl) {
          newMedia.push({
            type: 'image',
            file: {
              source: 'remote',
              url: slide.debugHtmlScreenshotUrl,
              key: `debug-html-layout-${Date.now()}-${i}`,
              file: { name: 'debug-html-layout.png', type: 'image/png', size: 0 },
            },
          })
        }
      }

      // Append to media list
      const updatedMedia = await new Promise<WorkspaceMedia[]>((resolve) => {
        setWorkspaceMedia((prev) => {
          const next = [...prev, ...newMedia]
          resolve(next)
          return next
        })
      })

      // Persist to database
      await updateWorkspace({
        id: workspace.id,
        data: { media: updatedMedia },
      })
      utils.workspace.get.invalidate({ id: workspace.id })

      const slideCount = result.slides.length
      toast.success(
        slideCount > 1
          ? autoDesignFormat === 'carousel'
            ? `Carousel ready (${slideCount} slides)! Click to edit.`
            : `${slideCount} designs added! Click to edit.`
          : 'Design generated! Click to edit.',
      )
    } catch (error) {
      console.error('Auto design generation failed:', error)
      toast.error('Failed to generate design. Please try again.')
    } finally {
      setIsAutoDesignGenerating(false)
      setAutoDesignPrompt('')
      setAutoDesignReferenceImage(null)
      setAutoDesignUseRefColors(false)
      setAutoDesignRefFidelity(50)
      setLocalGeneratingImages((prev) => prev.filter((g) => g.id !== 'auto-design'))
    }
  }

  const handleModifyDesign = async () => {
    if (!workspace || !autoDesignTarget || !modifyPrompt.trim()) return

    const { media: targetMedia, index: targetIndex } = autoDesignTarget

    // Extract existing image URLs from canvas nodes (photo + legacy image roles)
    const existingImages = targetMedia.nodes
      .filter(
        (n) =>
          n.data.type === 'image' && n.data.source.type === 'url' && n.metadata?.role === 'image',
      )
      .map((n) => (n.data as { source: { url: string } }).source.url)

    setIsAutoDesignGenerating(true)
    setIsAutoDesignDialogOpen(false)

    setLocalGeneratingImages((prev) => [
      ...prev,
      { id: 'auto-design-modify', prompt: 'Modifying design...' },
    ])

    try {
      // Fetch current rendered PNG and use it as reference
      const referenceImageBase64 = await fetchImageAsBase64(targetMedia.file.url)
      const captionForModify = autoDesignCaption.trim() || modifyPrompt.trim()

      // Every editor preset regenerates at its own shape. A custom ratio has no
      // preset to render, so it snaps to the nearest one — say so rather than
      // quietly handing back a different shape than the user is looking at.
      const generationAspectRatio = toPresetAspectRatio(targetMedia.aspectRatio)
      if (generationAspectRatio !== targetMedia.aspectRatio) {
        toast.info(
          `Regenerating at ${generationAspectRatio}, the closest shape design generation can render.`,
        )
      }

      const result = await generateCanvas({
        caption: captionForModify,
        prompt: modifyPrompt,
        referenceImage: referenceImageBase64,
        referenceFidelity: 90,
        existingImages: existingImages.length > 0 ? existingImages : undefined,
        aspectRatio: generationAspectRatio,
        workspaceId: workspace.id,
        moodboardId: workspace.moodboardId ?? undefined,
      })

      if (!result.ok) {
        toast.error('Usage limit exceeded. Please upgrade your plan.')
        return
      }

      // Render offscreen to get the PNG file
      const file = await renderCanvasOffscreen({
        nodes: result.nodes,
        aspectRatio: result.aspectRatio,
        aiProvenance: markAiEdited(targetMedia),
      })

      // Upload rendered PNG to S3
      const { key, uploadUrl, assetUrl } = await getUploadUrl({
        type: 'workspace',
        workspaceId: workspace.id,
      })
      await uploadToS3(uploadUrl, file)

      // Add the new design after the original (keep original intact)
      const newCanvasMedia: WorkspaceMedia = {
        type: 'canvas',
        nodes: result.nodes,
        aspectRatio: result.aspectRatio,
        file: {
          source: 'remote',
          url: assetUrl,
          key,
          file: { name: file.name, type: file.type, size: file.size },
        },
        // Redesigning around an existing asset is an edit of it, not a fresh
        // generation — keep what the source actually was.
        ...markAiEdited(targetMedia),
      }

      const updatedMedia = await new Promise<WorkspaceMedia[]>((resolve) => {
        setWorkspaceMedia((prev) => {
          const next = [
            ...prev.slice(0, targetIndex + 1),
            newCanvasMedia,
            ...prev.slice(targetIndex + 1),
          ]
          resolve(next)
          return next
        })
      })

      // Persist to database
      await updateWorkspace({
        id: workspace.id,
        data: { media: updatedMedia },
      })
      utils.workspace.get.invalidate({ id: workspace.id })

      toast.success('Design modified!')
    } catch (error) {
      console.error('Modify design failed:', error)
      toast.error('Failed to modify design. Please try again.')
    } finally {
      setIsAutoDesignGenerating(false)
      setAutoDesignTarget(null)
      setModifyPrompt('')
      setLocalGeneratingImages((prev) => prev.filter((g) => g.id !== 'auto-design-modify'))
    }
  }

  // render

  // The home flow parks on /workspace/temp-<timestamp> while the workspace is
  // being created, then routes to the real id. Redirecting that placeholder
  // home races the real navigation and can drop the user back on the home
  // screen with their new workspace created but never opened. The page already
  // renders nothing until the id resolves, so waiting costs nothing.
  useEffect(() => {
    if (isNaN(id) && !rawId?.startsWith('temp-')) navigate('/')
  }, [id, rawId, navigate])

  if (isNaN(id)) return null

  if (isWorkspaceLoading || isWorkspaceGenerating) {
    return (
      <EditorLoading
        isWorkspaceLoading={isWorkspaceLoading}
        isWorkspaceGenerating={isWorkspaceGenerating}
      />
    )
  }

  if (!workspace) return <div>Workspace not found</div>

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      {workspaceMode.type === 'editor' && (
        <WorkspaceEditor
          workspace={workspace}
          workspaceMedia={workspaceMedia}
          mediaIndex={workspaceMode.mediaIndex}
          setWorkspaceMedia={(media) => {
            workspaceMediaRef.current = media
            setWorkspaceMedia(media)
          }}
          onEditingDone={async () => {
            if (workspaceMode.thumbnailForVideoIndex != null) {
              // Thumbnail editor session — save canvas as workspace thumbnail,
              // then remove the temp canvas from media.
              const latestMedia = workspaceMediaRef.current
              const canvasMedia = latestMedia[workspaceMode.mediaIndex]
              if (canvasMedia?.type === 'canvas' && canvasMedia.nodes.length > 0) {
                try {
                  const prevTimestamp = workspace.thumbnail?.sourceTimestampMs
                  const thumbnail: ThumbnailCanvas = {
                    nodes: canvasMedia.nodes,
                    aspectRatio: canvasMedia.aspectRatio,
                    ...(canvasMedia.backgroundColor
                      ? { backgroundColor: canvasMedia.backgroundColor }
                      : {}),
                    ...(prevTimestamp != null ? { sourceTimestampMs: prevTimestamp } : {}),
                    file: canvasMedia.file,
                  }
                  const cleaned = latestMedia.filter((_, i) => i !== workspaceMode.mediaIndex)
                  setWorkspaceMedia(cleaned)
                  await updateWorkspace({
                    id: workspace.id,
                    data: { media: cleaned, thumbnail },
                  })
                  utils.workspace.get.invalidate({ id: workspace.id })
                } catch (e) {
                  console.error('Failed to save thumbnail from editor:', e)
                  const cleaned = latestMedia.filter((_, i) => i !== workspaceMode.mediaIndex)
                  setWorkspaceMedia(cleaned)
                  await updateWorkspace({ id: workspace.id, data: { media: cleaned } })
                }
              } else {
                // Empty canvas — clear the thumbnail
                const cleaned = latestMedia.filter((_, i) => i !== workspaceMode.mediaIndex)
                setWorkspaceMedia(cleaned)
                await updateWorkspace({
                  id: workspace.id,
                  data: { media: cleaned, thumbnail: null },
                })
                utils.workspace.get.invalidate({ id: workspace.id })
              }
            }
            originalMediaBeforeEditRef.current = null
            setWorkspaceMode({ type: 'media-manager' })
          }}
          onCancel={() => {
            // Restore original media if we have it stored (image was converted to canvas)
            if (originalMediaBeforeEditRef.current) {
              setWorkspaceMedia(originalMediaBeforeEditRef.current)
              originalMediaBeforeEditRef.current = null
            }
            // If this was a thumbnail editor session, clean up the temp canvas
            if (workspaceMode.thumbnailForVideoIndex != null) {
              const cleaned = workspaceMediaRef.current.filter(
                (_, i) => i !== workspaceMode.mediaIndex,
              )
              setWorkspaceMedia(cleaned)
              updateWorkspace({ id: workspace!.id, data: { media: cleaned } })
            }
            setWorkspaceMode({ type: 'media-manager' })
          }}
          autoOpenImageDialog={workspaceMode.autoOpenDialog ?? false}
        />
      )}

      {workspaceMode.type === 'media-manager' && (
        <div className="flex flex-col md:flex-row">
          <Header />
          <div
            ref={contentContainerRef}
            className="w-full transition-all duration-300 ease-in-out"
            style={{
              // Below 1024px the chat overlays the editor instead of sitting beside
              // it: 100vw - 480px goes negative on a phone and pushed the editor
              // (and its Post button) off-screen.
              maxWidth: isChatOpen && chatBesideEditor ? 'calc(100vw - 480px)' : '100vw',
            }}>
            <div className="mx-auto flex w-full max-w-[690px] flex-col items-center justify-center gap-4 px-4 pt-4 md:px-0 md:pt-[40px]">
              <WorkspaceHeader
                workspaceId={workspace.id}
                onUpdateScheduled={(entry) =>
                  updateScheduledContentRef.current?.(entry) ?? Promise.resolve()
                }
                isSaving={isSaving}
                media={workspaceMedia}
                captions={promptContext ?? {}}
                platformTitles={platformTitles}
                workspaceTitle={workspace.title}
                // The export has to ship the file that publishes, so it needs
                // the same label decision the publish path uses.
                bakeAiLabel={aiDisclosure.visibleLabel}
                aiLabelStyle={aiDisclosure.labelStyle}
                aiProvenance={aiProvenanceClaim}
                selectedCaptionPlatform={selectedCaptionPlatform}
                onCaptionPlatformChange={setSelectedCaptionPlatform}
                captionLength={{
                  instagram: promptContext?.instagram?.length || 0,
                  linkedin: promptContext?.linkedin?.length || 0,
                  tiktok: promptContext?.tiktok?.length || 0,
                  youtube: promptContext?.youtube?.length || 0,
                  facebook: promptContext?.facebook?.length || 0,
                  'meta-ads': promptContext?.['meta-ads']?.length || 0,
                }}
                onPreviewClick={() => setIsPreviewModalOpen(true)}
                activePlatforms={activePlatforms}
                onActivePlatformsChange={setActivePlatforms}
                onSettingsClick={() => setIsSettingsOpen(true)}
                onThumbnailClick={() => setIsThumbnailPickerOpen(true)}
                hasThumbnail={!!workspace.thumbnail}
                rating={postRatings[selectedCaptionPlatform]}
                isRatingLoading={isRatingLoading}
                onAnalyzeRating={() => ratingAnalyzeRef.current?.()}
              />

              <WorkspaceLayoutPost
                workspaceMedia={workspaceMedia}
                setMedia={handleSetMedia}
                workspaceId={workspace!.id}
                fileInputRef={fileInputRef}
                textareaRef={textareaRef}
                caption={promptContext?.[selectedCaptionPlatform] || ''}
                onCaptionChange={(value) => handleCaptionChange(value, selectedCaptionPlatform)}
                captionReadOnly={isCaptionLocked(selectedCaptionPlatform)}
                // Only meaningful when a label is actually going to be baked;
                // showing it for a post that publishes clean would be its own
                // kind of lie.
                showAiLabel={aiDisclosure.visibleLabel && aiDisclosure.displayInWorkspace}
                aiLabelStyle={aiDisclosure.labelStyle}
                isSaving={isSaving}
                selectedCaptionPlatform={selectedCaptionPlatform}
                title={platformTitles?.[selectedCaptionPlatform] || ''}
                onTitleChange={
                  selectedCaptionPlatform === 'youtube'
                    ? (value) => handleTitleChange(value, 'youtube')
                    : undefined
                }
                onEdit={async (media: WorkspaceMedia, index: number) => {
                  if (guardMediaLocked()) return
                  if (media.type === 'canvas') {
                    setWorkspaceMode({ type: 'editor', mediaIndex: index })
                    return
                  }

                  if (media.type === 'image') {
                    // Store original media before converting to canvas (for cancel restoration)
                    originalMediaBeforeEditRef.current = workspaceMedia

                    // Pick the supported aspect ratio closest to the source image so the
                    // canvas doesn't visibly snap to a different shape on entering the editor.
                    let aspectRatio = '1:1'
                    try {
                      const dims = await loadImageDimensionsFromUrl(media.file.url)
                      aspectRatio = presetAspectRatioForSize(dims.width, dims.height)
                    } catch {
                      // Fall back to 1:1 if the image fails to load.
                    }

                    const canvasMedia = imageToCanvas(media.file, aspectRatio)
                    const updatedMedia = replaceAtIndex(workspaceMedia, index, canvasMedia)

                    setWorkspaceMedia(updatedMedia)
                    setWorkspaceMode({
                      type: 'editor',
                      mediaIndex: index,
                    })
                    return
                  }

                  toast.info('Editing is only available for images')
                }}
                onAddMedia={() => {
                  if (guardMediaLocked()) return
                  setIsAddMediaModalOpen(true)
                }}
                onAutoDesign={async (media, index) => {
                  if (guardMediaLocked()) return
                  if (media.type === 'canvas' && media.nodes.length > 0) {
                    setAutoDesignTarget({ media, index })
                    setModifyPrompt('')
                  } else {
                    setAutoDesignTarget(null)
                    // Pre-fill the image as a reference for generateCanvas
                    if (media.type === 'image' && media.file.url) {
                      try {
                        const base64 = await fetchImageAsBase64(media.file.url)
                        setAutoDesignReferenceImage(base64)
                        setAutoDesignRefFidelity(75)
                      } catch {
                        // Non-critical, open dialog without reference
                      }
                    }
                  }
                  setIsAutoDesignDialogOpen(true)
                }}
                generatingImages={generatingImages}
              />
            </div>
          </div>
          <div
            className="fixed top-0 h-screen w-full max-w-[480px] bg-transparent p-2 transition-all duration-300 ease-in-out"
            style={{ right: isChatOpen ? '0px' : '-480px' }}>
            {/* When the chat overlays the editor it covers the header's chat toggle,
                so it needs its own way out. */}
            {!chatBesideEditor && isChatOpen && (
              <Button
                variant="outline"
                size="icon-sm"
                onClick={closeChat}
                className="absolute right-4 top-4 z-20 rounded-full"
                aria-label="Close chat">
                <XIcon size={16} />
              </Button>
            )}
            {workspace && (
              <WorkspaceChat
                key={workspace.id}
                workspaceId={workspace.id}
                moodboardId={workspace.moodboardId}
                captions={promptContext ?? {}}
                media={workspaceMedia}
                youtubeTitle={platformTitles?.youtube}
                selectedCaptionPlatform={selectedCaptionPlatform}
                connectedAccounts={connectedAccounts}
                chatHistory={workspace?.chatHistory}
                onCaptionChange={(value, platform) => handleCaptionChange(value, platform)}
                onTitleChange={(value, platform) => handleTitleChange(value, platform)}
                onAgentEditingChange={(active) => {
                  agentEditingRef.current = active
                }}
                onCaptionStream={(platform, text, youtubeTitle) => {
                  // Live editor state only — persistence happens once in
                  // onPostGenerated, not per streamed chunk.
                  setPromptContext((prev) => {
                    const next = { ...prev, [platform]: text }
                    // If the user typed mid-stream, a debounced save is already
                    // scheduled with a snapshot — keep it current so it can't
                    // persist a stale partial caption.
                    if (pendingPromptContextRef.current) {
                      pendingPromptContextRef.current = next
                    }
                    return next
                  })
                  if (youtubeTitle) {
                    setPlatformTitles((prev) => ({ ...prev, youtube: youtubeTitle }))
                  }
                  setActivePlatforms((prev) =>
                    prev.includes(platform) ? prev : [...prev, platform],
                  )
                }}
                onPostPlan={(plan) => {
                  setActivePlatforms(plan.platforms)
                  setSelectedCaptionPlatform((prev) =>
                    plan.platforms.includes(prev) ? prev : (plan.platforms[0] ?? prev),
                  )
                }}
                onPostGenerated={async (payload, userMedia) => {
                  // The write below is authoritative — a debounced save from a
                  // mid-stream user edit must not land after it with a stale
                  // partial snapshot.
                  if (saveCaptionTimeoutRef.current) {
                    clearTimeout(saveCaptionTimeoutRef.current)
                    saveCaptionTimeoutRef.current = null
                  }
                  pendingPromptContextRef.current = null
                  pendingTitlesRef.current = null
                  setIsSaving(false)

                  const captions: Partial<Record<PlatformType, string>> = {}
                  const titles: Partial<Record<PlatformType, string>> = {}
                  for (const p of [
                    'instagram',
                    'linkedin',
                    'tiktok',
                    'facebook',
                    'meta-ads',
                  ] as const) {
                    const caption = payload.captions[p]
                    if (caption) captions[p] = caption
                  }
                  if (payload.captions.youtube) {
                    captions.youtube = payload.captions.youtube.description
                    titles.youtube = payload.captions.youtube.title
                  }

                  setPromptContext((prev) => ({ ...prev, ...captions }))
                  setPlatformTitles((prev) => ({ ...prev, ...titles }))
                  setActivePlatforms(payload.platforms)
                  setSelectedCaptionPlatform((prev) =>
                    payload.platforms.includes(prev) ? prev : (payload.platforms[0] ?? prev),
                  )

                  const mergedMedia =
                    userMedia.length > 0 ? [...workspaceMediaRef.current, ...userMedia] : null
                  if (mergedMedia) setWorkspaceMedia(mergedMedia)

                  await updateWorkspace({
                    id: workspace.id,
                    data: {
                      title: payload.title,
                      captions,
                      titles,
                      activePlatforms: payload.platforms,
                      status: 'success',
                      ...(mergedMedia ? { media: mergedMedia } : {}),
                    },
                    generateTitle: true,
                  })
                  utils.workspace.list.invalidate()
                  utils.workspace.get.invalidate({ id: workspace.id })

                  toast.success('Post generated successfully!', {
                    description: 'Your post is ready for editing.',
                    duration: 7000,
                  })
                }}
                onRatingChange={(rating) => {
                  setPostRatings((prev) => ({ ...prev, [rating.platform]: rating }))
                  setSelectedCaptionPlatform(rating.platform)
                  setActivePlatforms((prev) =>
                    prev.includes(rating.platform) ? prev : [...prev, rating.platform],
                  )
                  setIsRatingLoading(false)
                }}
                onRatingLoadingChange={setIsRatingLoading}
                onRatingAnalyzeRef={ratingAnalyzeRef}
                isRatingLoading={isRatingLoading}
                onDesignGenerating={(id, prompt) => {
                  setLocalGeneratingImages((prev) => [...prev, { id, prompt }])
                }}
                onDesignGenerated={async (id, result) => {
                  setLocalGeneratingImages((prev) => prev.filter((g) => g.id !== id))

                  try {
                    // A single graphic yields one slide; a carousel yields
                    // several. Render each slide offscreen, upload it, and
                    // append one canvas media item per slide in reading order.
                    const newCanvasMedia: WorkspaceMedia[] = []
                    for (const slide of result.slides) {
                      const nodes = slide.nodes as LayoutNode[]

                      const file = await renderCanvasOffscreen({
                        nodes,
                        aspectRatio: slide.aspectRatio,
                        aiProvenance: markAiGenerated(),
                      })

                      const { key, uploadUrl, assetUrl } = await getUploadUrl({
                        type: 'workspace',
                        workspaceId: workspace.id,
                      })
                      await uploadToS3(uploadUrl, file)

                      newCanvasMedia.push({
                        type: 'canvas',
                        nodes,
                        aspectRatio: slide.aspectRatio,
                        file: {
                          source: 'remote',
                          url: assetUrl,
                          key,
                          file: { name: file.name, type: file.type, size: file.size },
                        },
                        ...markAiGenerated(),
                      })
                    }

                    const updatedMedia = await new Promise<WorkspaceMedia[]>((resolve) => {
                      setWorkspaceMedia((prev) => {
                        const next = [...prev, ...newCanvasMedia]
                        resolve(next)
                        return next
                      })
                    })

                    toast.success(
                      newCanvasMedia.length > 1
                        ? `${newCanvasMedia.length} designs added! Click to edit.`
                        : 'Design generated! Click to edit.',
                    )

                    await updateWorkspace({
                      id: workspace.id,
                      data: { media: updatedMedia },
                    })
                    utils.workspace.get.invalidate({ id: workspace.id })
                  } catch (error) {
                    console.error('Failed to process design:', error)
                    toast.error('Failed to save design. Please try again.')
                  }
                }}
                onDesignGenerationFailed={(id) => {
                  setLocalGeneratingImages((prev) => prev.filter((g) => g.id !== id))
                  toast.error('Failed to generate design. Please try again.')
                }}
                onDesignEditingDone={(id) => {
                  setLocalGeneratingImages((prev) => prev.filter((g) => g.id !== id))
                }}
                onReplaceMedia={async (index, newMedia) => {
                  const updated = workspaceMediaRef.current.map((m, i) =>
                    i === index ? newMedia : m,
                  )
                  setWorkspaceMedia(updated)
                  await updateWorkspace({ id: workspace.id, data: { media: updated } })
                  utils.workspace.get.invalidate({ id: workspace.id })
                }}
                onAppendMedia={async (newMedia) => {
                  const updated = [...workspaceMediaRef.current, newMedia]
                  setWorkspaceMedia(updated)
                  await updateWorkspace({ id: workspace.id, data: { media: updated } })
                  utils.workspace.get.invalidate({ id: workspace.id })
                }}
                onPublishPost={async (input) => {
                  // Agent-driven immediate publish. Platform settings use the
                  // backend defaults — the preview modal remains the surface
                  // for advanced per-platform settings.
                  if (!isPublishablePlatform(input.platform)) {
                    return { success: false, error: `${input.platform} cannot be published.` }
                  }

                  const built = buildPostContentFromWorkspace(
                    {
                      title: workspace.title ?? '',
                      captions: promptContext ?? {},
                      titles: platformTitles ?? {},
                      media: workspaceMedia,
                    },
                    input.platform,
                  )
                  if (!built.ok) return { success: false, error: built.reason }
                  if (built.content.platform !== input.platform) {
                    return { success: false, error: 'Internal error: built platform mismatch' }
                  }

                  try {
                    const result = await createPlatformPost({
                      ...built.content,
                      accountId: input.accountId,
                    })
                    utils.workspace.get.invalidate({ id: workspace.id })
                    return { success: true, link: result.link || undefined }
                  } catch (error) {
                    const message = error instanceof Error ? error.message : String(error)
                    return {
                      success: false,
                      error: message.replace(/^TRPCClientError:\s*/i, ''),
                    }
                  }
                }}
                onSchedulePost={async (input) => {
                  const scheduledAt = new Date(input.scheduledAt)
                  if (scheduledAt.getTime() <= Date.now()) {
                    return { success: false, error: 'Scheduled time must be in the future.' }
                  }
                  // Generation-only targets (e.g. Meta Ads) can't be scheduled/published.
                  if (!isPublishablePlatform(input.platform)) {
                    return { success: false, error: `${input.platform} cannot be scheduled.` }
                  }

                  // The shared builder is the same one the preview modal and
                  // the API/MCP path use — single source of truth for
                  // workspace → postContent conversion.
                  const built = buildPostContentFromWorkspace(
                    {
                      title: workspace.title ?? '',
                      captions: promptContext ?? {},
                      titles: platformTitles ?? {},
                      media: workspaceMedia,
                    },
                    input.platform,
                  )
                  if (!built.ok) return { success: false, error: built.reason }
                  if (built.content.platform !== input.platform) {
                    return { success: false, error: 'Internal error: built platform mismatch' }
                  }

                  // Copy single-URL postContent assets into a scheduledPost-
                  // scoped S3 folder so the publish worker has a stable URL
                  // when the schedule fires. Multi-URL shapes (carousels,
                  // multiImage, multiPhoto) keep their workspace URLs and get
                  // copied at publish time.
                  //
                  // The create + copy + update sequence isn't atomic — if
                  // copy or update fails after the row is committed, we'd
                  // leak a scheduled_post pointing to a workspace-scoped URL
                  // that the publish worker can't dereference once the
                  // workspace assets get cleaned up. Wrap the post-create
                  // work in a try/catch that rolls back the row on failure.
                  let scheduled: { id: number } | undefined
                  try {
                    switch (built.content.platform) {
                      case 'linkedin': {
                        const c = built.content.postContent
                        scheduled = await createScheduledPost({
                          platform: 'linkedin',
                          accountId: input.accountId,
                          postContent: c,
                          scheduledAt,
                        })
                        if ((c.type === 'image' || c.type === 'video') && c.url) {
                          const { url } = await copyAsset({
                            sourceUrl: c.url,
                            destination: { type: 'scheduledPost', scheduledPostId: scheduled.id },
                          })
                          await updateScheduledPost({
                            id: scheduled.id,
                            platform: 'linkedin',
                            postContent: { ...c, url },
                          })
                        }
                        break
                      }
                      case 'instagram': {
                        const c = built.content.postContent
                        scheduled = await createScheduledPost({
                          platform: 'instagram',
                          accountId: input.accountId,
                          postContent: c,
                          scheduledAt,
                        })
                        if (c.type !== 'carousel' && 'url' in c && c.url) {
                          const { url } = await copyAsset({
                            sourceUrl: c.url,
                            destination: { type: 'scheduledPost', scheduledPostId: scheduled.id },
                          })
                          await updateScheduledPost({
                            id: scheduled.id,
                            platform: 'instagram',
                            postContent: { ...c, url },
                          })
                        }
                        break
                      }
                      case 'tiktok': {
                        const c = built.content.postContent
                        scheduled = await createScheduledPost({
                          platform: 'tiktok',
                          accountId: input.accountId,
                          postContent: c,
                          scheduledAt,
                        })
                        if ('url' in c && c.url) {
                          const { url } = await copyAsset({
                            sourceUrl: c.url,
                            destination: { type: 'scheduledPost', scheduledPostId: scheduled.id },
                          })
                          await updateScheduledPost({
                            id: scheduled.id,
                            platform: 'tiktok',
                            postContent: { ...c, url },
                          })
                        }
                        break
                      }
                      case 'youtube': {
                        const c = built.content.postContent
                        scheduled = await createScheduledPost({
                          platform: 'youtube',
                          accountId: input.accountId,
                          postContent: c,
                          scheduledAt,
                        })
                        const { url } = await copyAsset({
                          sourceUrl: c.url,
                          destination: { type: 'scheduledPost', scheduledPostId: scheduled.id },
                        })
                        await updateScheduledPost({
                          id: scheduled.id,
                          platform: 'youtube',
                          postContent: { ...c, url },
                        })
                        break
                      }
                      case 'facebook': {
                        const c = built.content.postContent
                        scheduled = await createScheduledPost({
                          platform: 'facebook',
                          accountId: input.accountId,
                          postContent: c,
                          scheduledAt,
                        })
                        if (
                          c.type !== 'text' &&
                          c.type !== 'link' &&
                          c.type !== 'multiPhoto' &&
                          'url' in c &&
                          c.url
                        ) {
                          const { url } = await copyAsset({
                            sourceUrl: c.url,
                            destination: { type: 'scheduledPost', scheduledPostId: scheduled.id },
                          })
                          await updateScheduledPost({
                            id: scheduled.id,
                            platform: 'facebook',
                            postContent: { ...c, url },
                          })
                        }
                        break
                      }
                    }
                  } catch (err) {
                    if (scheduled) {
                      // Best-effort rollback. If the delete also fails we
                      // surface the original publish-prep error rather than
                      // the cleanup failure — that one's the user-actionable
                      // signal.
                      await deleteScheduledPost({ id: scheduled.id }).catch(() => {})
                    }
                    return {
                      success: false,
                      error: err instanceof Error ? err.message : 'Failed to schedule post',
                    }
                  }

                  if (!scheduled) {
                    return {
                      success: false,
                      error: 'Internal error: schedule did not complete',
                    }
                  }

                  toast.success('Post scheduled!')
                  utils.scheduledPost.list.invalidate()
                  return {
                    success: true,
                    scheduledPostId: scheduled.id,
                    platform: input.platform,
                    scheduledAt: input.scheduledAt,
                  }
                }}
              />
            )}
          </div>
        </div>
      )}

      <WorkspaceMediaModal
        open={isAddMediaModalOpen}
        onUploadClick={() => fileInputRef.current?.click()}
        onClose={() => setIsAddMediaModalOpen(false)}
        onGenerateClick={() => {
          const newCanvas = createEmptyCanvasMedia()
          const newMediaIndex = workspaceMedia.length
          setWorkspaceMedia([...workspaceMedia, newCanvas])
          setWorkspaceMode({ type: 'editor', mediaIndex: newMediaIndex, autoOpenDialog: true })
        }}
        onEmptyCanvasClick={() => {
          const newCanvas = createEmptyCanvasMedia()
          const newMediaIndex = workspaceMedia.length
          setWorkspaceMedia([...workspaceMedia, newCanvas])
          setWorkspaceMode({ type: 'editor', mediaIndex: newMediaIndex })
        }}
        onGenerateDesignClick={() => {
          setAutoDesignTarget(null)
          setIsAutoDesignDialogOpen(true)
        }}
        onGenerateVideoClick={() => setIsVideoDialogOpen(true)}
        hasImages={workspaceMedia.some((m) => m.type === 'image' || m.type === 'canvas')}
      />

      <WorkspacePreviewModal
        open={isPreviewModalOpen}
        onOpenChange={setIsPreviewModalOpen}
        updateScheduledContentRef={updateScheduledContentRef}
        accounts={{
          linkedin: linkedinData?.isAuthenticated ? linkedinData.accounts : [],
          instagram: instagramData?.isAuthenticated ? instagramData.accounts : [],
          tiktok: tiktokData?.isAuthenticated ? tiktokData.accounts : [],
          youtube: youtubeData?.isAuthenticated ? youtubeData.accounts : [],
          facebook: facebookData?.isAuthenticated ? facebookData.accounts : [],
        }}
        isAuthenticated={{
          linkedin: linkedinData?.isAuthenticated ?? false,
          instagram: instagramData?.isAuthenticated ?? false,
          tiktok: tiktokData?.isAuthenticated ?? false,
          youtube: youtubeData?.isAuthenticated ?? false,
          facebook: facebookData?.isAuthenticated ?? false,
        }}
        workspaceMedia={workspaceMedia}
        captions={promptContext || {}}
        activePlatforms={activePlatforms}
        onActivePlatformsChange={setActivePlatforms}
        onSettingsClick={() => setIsSettingsOpen(true)}
        onOpenSettingsForConfirm={(callback, label) => {
          settingsConfirmCallbackRef.current = callback
          setSettingsConfirmLabel(label)
          setSettingsConfirmMode(true)
          setIsSettingsOpen(true)
        }}
        postAsCarousel={postAsCarousel}
        publishAsDraft={publishAsDraft}
        workspaceId={workspace?.id}
        aiLabelStyle={aiDisclosure.labelStyle}
        showAiLabelInPreview={aiDisclosure.displayInPreview}
        platformSettings={{
          instagram: {
            postAsStory: instagramPostAsStory,
            shareToFeed: instagramShareToFeed,
            disableComments: instagramDisableComments,
            locationId: instagramLocationId,
            isAiGenerated: aiDisclosurePlans.instagram.nativeFlag,
            bakeAiLabel: aiDisclosurePlans.instagram.bakedLabel,
            aiLabelStyle: aiDisclosure.labelStyle,
            aiProvenance: aiProvenanceClaim ?? undefined,
          },
          linkedin: {
            visibility: linkedinVisibility,
            disableReshare: linkedinDisableReshare,
            captionsUrl: linkedinCaptionsUrl,
            bakeAiLabel: aiDisclosurePlans.linkedin.bakedLabel,
            aiLabelStyle: aiDisclosure.labelStyle,
            aiProvenance: aiProvenanceClaim ?? undefined,
          },
          tiktok: {
            creatorInfo: tiktokCreatorInfo,
            onCreatorInfoChange: setTiktokCreatorInfo,
            privacyLevel: tiktokPrivacyLevel,
            allowComments: tiktokAllowComments,
            allowDuets: tiktokAllowDuets,
            allowStitches: tiktokAllowStitches,
            commercialContent: tiktokCommercialContent,
            yourBrand: tiktokYourBrand,
            brandedContent: tiktokBrandedContent,
            isAigc: aiDisclosurePlans.tiktok.nativeFlag,
            bakeAiLabel: aiDisclosurePlans.tiktok.bakedLabel,
            aiLabelStyle: aiDisclosure.labelStyle,
            aiProvenance: aiProvenanceClaim ?? undefined,
            postMode: tiktokPostMode,
            autoAddMusic: tiktokAutoAddMusic,
          },
          youtube: {
            privacy: youtubePrivacy,
            postAsShort: youtubePostAsShort,
            category: youtubeCategory,
            containsSyntheticMedia: aiDisclosurePlans.youtube.nativeFlag,
            defaultLanguage: youtubeDefaultLanguage,
            defaultAudioLanguage: youtubeDefaultAudioLanguage,
            notifySubscribers: youtubeNotifySubscribers,
            license: youtubeLicense,
            embeddable: youtubeEmbeddable,
            publicStatsViewable: youtubePublicStatsViewable,
            localizationsJson: youtubeLocalizationsJson,
            captionsUrl: youtubeCaptionsUrl,
            captionsLanguage: youtubeCaptionsLanguage,
            playlistId: youtubePlaylistId,
          },
          facebook: {
            postAsDraft: facebookPostAsDraft,
            postAsStory: facebookPostAsStory,
            postAsReel: facebookPostAsReel,
            placeId: facebookPlaceId,
            feedTargetingJson: facebookFeedTargetingJson,
            targetingJson: facebookTargetingJson,
            videoTitle: facebookVideoTitle,
            contentCategory: facebookContentCategory,
            // Facebook wants the shape of AI involvement, not just a boolean.
            // Derived from provenance so the single toggle stays a single
            // toggle. See `platforms/aiDisclosure.ts`.
            aiDisclosure: aiDisclosurePlans.facebook.nativeFlag
              ? resolveAiInvolvement(workspaceMedia)
              : undefined,
            bakeAiLabel: aiDisclosurePlans.facebook.bakedLabel,
            aiLabelStyle: aiDisclosure.labelStyle,
            aiProvenance: aiProvenanceClaim ?? undefined,
          },
        }}
        workspaceTitle={workspace.title}
        platformTitles={platformTitles}
        thumbnail={workspace.thumbnail}
      />

      <WorkspaceSettingsModal
        postAsCarousel={postAsCarousel}
        onPostAsCarouselChange={setPostAsCarousel}
        publishAsDraft={publishAsDraft}
        onPublishAsDraftChange={setPublishAsDraft}
        isYoutubeActive={activePlatforms.includes('youtube')}
        youtubePrivacy={youtubePrivacy}
        onYoutubePrivacyChange={setYoutubePrivacy}
        youtubePostAsShort={youtubePostAsShort}
        onYoutubePostAsShortChange={setYoutubePostAsShort}
        youtubeCategory={youtubeCategory}
        onYoutubeCategoryChange={setYoutubeCategory}
        isInstagramActive={activePlatforms.includes('instagram')}
        instagramPostAsStory={instagramPostAsStory}
        onInstagramPostAsStoryChange={setInstagramPostAsStory}
        instagramShareToFeed={instagramShareToFeed}
        onInstagramShareToFeedChange={setInstagramShareToFeed}
        instagramDisableComments={instagramDisableComments}
        onInstagramDisableCommentsChange={setInstagramDisableComments}
        instagramLocationId={instagramLocationId}
        onInstagramLocationIdChange={setInstagramLocationId}
        isLinkedinActive={activePlatforms.includes('linkedin')}
        linkedinVisibility={linkedinVisibility}
        onLinkedinVisibilityChange={setLinkedinVisibility}
        linkedinDisableReshare={linkedinDisableReshare}
        onLinkedinDisableReshareChange={setLinkedinDisableReshare}
        linkedinCaptionsUrl={linkedinCaptionsUrl}
        linkedinCaptionsKey={linkedinCaptionsKey}
        onLinkedinCaptionsChange={({ url, key }) => {
          setLinkedinCaptionsUrl(url)
          setLinkedinCaptionsKey(key)
        }}
        isFacebookActive={activePlatforms.includes('facebook')}
        facebookPostAsDraft={facebookPostAsDraft}
        onFacebookPostAsDraftChange={setFacebookPostAsDraft}
        facebookPostAsStory={facebookPostAsStory}
        onFacebookPostAsStoryChange={setFacebookPostAsStory}
        facebookPostAsReel={facebookPostAsReel}
        onFacebookPostAsReelChange={setFacebookPostAsReel}
        facebookPlaceId={facebookPlaceId}
        onFacebookPlaceIdChange={setFacebookPlaceId}
        facebookFeedTargetingJson={facebookFeedTargetingJson}
        onFacebookFeedTargetingJsonChange={setFacebookFeedTargetingJson}
        facebookTargetingJson={facebookTargetingJson}
        onFacebookTargetingJsonChange={setFacebookTargetingJson}
        facebookVideoTitle={facebookVideoTitle}
        onFacebookVideoTitleChange={setFacebookVideoTitle}
        facebookContentCategory={facebookContentCategory}
        onFacebookContentCategoryChange={setFacebookContentCategory}
        isOpen={isSettingsOpen}
        onOpenChange={(open) => {
          setIsSettingsOpen(open)
          if (!open) {
            setSettingsConfirmMode(false)
            settingsConfirmCallbackRef.current = null
          }
        }}
        confirmMode={settingsConfirmMode}
        confirmLabel={settingsConfirmLabel}
        onConfirm={() => {
          setIsSettingsOpen(false)
          setSettingsConfirmMode(false)
          settingsConfirmCallbackRef.current?.()
          settingsConfirmCallbackRef.current = null
        }}
        imageCount={workspaceMedia.filter((m) => m.type === 'image' || m.type === 'canvas').length}
        videoCount={workspaceMedia.filter((m) => m.type === 'video').length}
        workspaceId={workspace?.id}
        isTiktokActive={activePlatforms.includes('tiktok')}
        tiktokCreatorInfo={tiktokCreatorInfo}
        isTiktokCreatorInfoLoading={isTiktokCreatorInfoLoading}
        tiktokCreatorInfoError={tiktokCreatorInfoError?.message}
        tiktokPrivacyLevel={tiktokPrivacyLevel}
        onTiktokPrivacyLevelChange={setTiktokPrivacyLevel}
        tiktokAllowComments={tiktokAllowComments}
        onTiktokAllowCommentsChange={setTiktokAllowComments}
        tiktokAllowDuets={tiktokAllowDuets}
        onTiktokAllowDuetsChange={setTiktokAllowDuets}
        tiktokAllowStitches={tiktokAllowStitches}
        onTiktokAllowStitchesChange={setTiktokAllowStitches}
        tiktokCommercialContent={tiktokCommercialContent}
        onTiktokCommercialContentChange={setTiktokCommercialContent}
        tiktokYourBrand={tiktokYourBrand}
        onTiktokYourBrandChange={setTiktokYourBrand}
        tiktokBrandedContent={tiktokBrandedContent}
        onTiktokBrandedContentChange={setTiktokBrandedContent}
        aiDisclosure={aiDisclosure}
        onAiDisclosureChange={(next) => {
          setAiDisclosure(next)
          setAiLabelAutoDetected(false)
        }}
        aiLabelAutoDetected={aiLabelAutoDetected}
        aiDetection={aiDetection}
        aiSavedLabelStyle={savedAiLabelStyle}
        onSaveAiLabelStyle={(style) => void updateBrand({ aiLabelStyle: style })}
        onClearAiLabelStyle={() => void updateBrand({ aiLabelStyle: null })}
        aiLabelHasImageMedia={aiLabelHasImageMedia}
        tiktokPostMode={tiktokPostMode}
        onTiktokPostModeChange={setTiktokPostMode}
        tiktokAutoAddMusic={tiktokAutoAddMusic}
        onTiktokAutoAddMusicChange={setTiktokAutoAddMusic}
        youtubeDefaultLanguage={youtubeDefaultLanguage}
        onYoutubeDefaultLanguageChange={setYoutubeDefaultLanguage}
        youtubeDefaultAudioLanguage={youtubeDefaultAudioLanguage}
        onYoutubeDefaultAudioLanguageChange={setYoutubeDefaultAudioLanguage}
        youtubeNotifySubscribers={youtubeNotifySubscribers}
        onYoutubeNotifySubscribersChange={setYoutubeNotifySubscribers}
        youtubeLicense={youtubeLicense}
        onYoutubeLicenseChange={setYoutubeLicense}
        youtubeEmbeddable={youtubeEmbeddable}
        onYoutubeEmbeddableChange={setYoutubeEmbeddable}
        youtubePublicStatsViewable={youtubePublicStatsViewable}
        onYoutubePublicStatsViewableChange={setYoutubePublicStatsViewable}
        youtubeLocalizationsJson={youtubeLocalizationsJson}
        onYoutubeLocalizationsJsonChange={setYoutubeLocalizationsJson}
        youtubeCaptionsUrl={youtubeCaptionsUrl}
        youtubeCaptionsKey={youtubeCaptionsKey}
        onYoutubeCaptionsChange={({ url, key }) => {
          setYoutubeCaptionsUrl(url)
          setYoutubeCaptionsKey(key)
        }}
        youtubeCaptionsLanguage={youtubeCaptionsLanguage}
        onYoutubeCaptionsLanguageChange={setYoutubeCaptionsLanguage}
        youtubePlaylistId={youtubePlaylistId}
        onYoutubePlaylistIdChange={setYoutubePlaylistId}
      />

      <VideoSlideshowDialog
        open={isVideoDialogOpen}
        onOpenChange={setIsVideoDialogOpen}
        workspaceMedia={workspaceMedia}
        workspaceId={workspace.id}
        onVideoGenerated={async (videoUrl, key, size) => {
          const newMedia: WorkspaceMedia = {
            type: 'video',
            file: {
              source: 'remote',
              url: videoUrl,
              key,
              file: { name: 'generated-video.mp4', type: 'video/mp4', size },
            },
            ...markAiGenerated(),
          }

          // Use functional update to get latest media state
          const updatedMedia = await new Promise<WorkspaceMedia[]>((resolve) => {
            setWorkspaceMedia((prev) => {
              const next = [...prev, newMedia]
              resolve(next)
              return next
            })
          })

          // Persist to database
          await updateWorkspace({
            id: workspace.id,
            data: { media: updatedMedia },
          })
          utils.workspace.get.invalidate({ id: workspace.id })
        }}
      />

      <VideoThumbnailPicker
        open={isThumbnailPickerOpen}
        onOpenChange={setIsThumbnailPickerOpen}
        thumbnail={workspace.thumbnail}
        videoUrl={workspaceMedia.find((m) => m.type === 'video')?.file.url ?? null}
        onThumbnailSet={async (url, key, sourceTimestampMs) => {
          const videoItem = workspaceMedia.find((m) => m.type === 'video')
          if (!videoItem) return
          const ar = await getVideoAspectRatio(videoItem.file.url)
          const thumb = createThumbnailCanvasFromImage(url, key, ar)
          if (sourceTimestampMs != null) thumb.sourceTimestampMs = sourceTimestampMs
          handleSetThumbnail(thumb)
        }}
        onThumbnailClear={() => handleSetThumbnail(null)}
        onUploadFile={async (file) => {
          const { key, uploadUrl, assetUrl } = await getUploadUrl({
            type: 'workspace',
            workspaceId: workspace.id,
          })
          await uploadToS3(uploadUrl, file)
          return { url: assetUrl, key }
        }}
        onOpenEditor={async () => {
          const videoItem = workspaceMedia.find((m) => m.type === 'video')
          if (!videoItem) return
          // Reuse existing thumbnail canvas if available, otherwise create
          // empty canvas matching the video's aspect ratio
          let canvas: Extract<WorkspaceMedia, { type: 'canvas' }>
          if (workspace.thumbnail) {
            canvas = { type: 'canvas', ...workspace.thumbnail }
          } else {
            const ar = await getVideoAspectRatio(videoItem.file.url)
            canvas = { ...createEmptyCanvasMedia(), aspectRatio: ar }
          }
          const newMediaIndex = workspaceMedia.length
          setWorkspaceMedia([...workspaceMedia, canvas])
          setWorkspaceMode({
            type: 'editor',
            mediaIndex: newMediaIndex,
            thumbnailForVideoIndex: 0,
          })
        }}
      />

      <Dialog
        open={isAutoDesignDialogOpen}
        onOpenChange={(open) => {
          setIsAutoDesignDialogOpen(open)
          if (!open) {
            setAutoDesignTarget(null)
            // Clear suggestions so the next open re-fetches against the
            // (possibly edited) caption rather than flashing stale picks.
            setSuggestedReferences(null)
          }
        }}>
        <DialogContent
          className={cn(
            'gap-0 overflow-hidden p-0 sm:max-w-[600px]',
            theme === 'dark' ? 'dark' : '',
          )}>
          {autoDesignTarget ? (
            <>
              <DialogHeader className="border-border space-y-1 border-b px-6 pb-5 pt-6">
                <DialogTitle>Modify design</DialogTitle>
                <p className="text-muted-foreground text-sm">
                  Describe a change, or pick a new visual direction.
                </p>
              </DialogHeader>

              <div className="flex flex-col">
                <section className="border-border flex items-center gap-3 border-b px-6 py-4">
                  <img
                    src={autoDesignTarget.media.file.url}
                    crossOrigin="anonymous"
                    className="border-border h-16 w-16 shrink-0 rounded-md border object-contain"
                  />
                  <p className="text-muted-foreground text-sm">Current design</p>
                </section>

                <section className="border-border flex flex-col gap-2 border-b px-6 pb-4 pt-5">
                  <Label>What should change?</Label>
                  <Textarea
                    placeholder="e.g. Move the headline to the top, swap the background, tighten the type…"
                    value={modifyPrompt}
                    onChange={(e) => setModifyPrompt(e.target.value)}
                    rows={2}
                  />
                </section>

                <section className="flex flex-col gap-3 px-6 pb-6 pt-5">
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground font-normal">
                      {suggestedReferences ? 'Suggested for this post' : 'Or restyle as'}
                    </Label>
                    <button
                      type="button"
                      onClick={handleSuggestReferences}
                      disabled={!canSuggest || isLoadingReferences}
                      className="hover:bg-hover text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                      title={
                        canSuggest
                          ? 'Suggest references tailored to this caption'
                          : 'Write a caption to enable suggestions'
                      }>
                      <Sparkles className="h-3 w-3" />
                      {isLoadingReferences
                        ? 'Suggesting…'
                        : suggestedReferences
                          ? 'Refresh'
                          : 'Suggest for this post'}
                    </button>
                  </div>
                  <ol
                    className={cn(
                      'grid grid-cols-2 gap-x-6 gap-y-0.5 transition-opacity',
                      isLoadingReferences && 'opacity-60',
                    )}>
                    {referenceChips.map((chip, i) => (
                      <li key={chip}>
                        <button
                          type="button"
                          onClick={() => setModifyPrompt(`Restyle as ${chip}`)}
                          className="hover:bg-hover group flex w-full items-baseline gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors">
                          <span className="text-muted-foreground font-mono text-[10px] tabular-nums">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <span className="text-sm">{chip}</span>
                        </button>
                      </li>
                    ))}
                  </ol>
                </section>
              </div>

              <div className="border-border flex items-center justify-end border-t px-6 py-4">
                <Button
                  variant="primary"
                  onClick={handleModifyDesign}
                  disabled={!modifyPrompt.trim() || isAutoDesignGenerating}>
                  {isAutoDesignGenerating ? 'Modifying...' : 'Modify Design'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader className="border-border space-y-1 border-b px-6 pb-5 pt-6">
                <DialogTitle>Auto Design</DialogTitle>
                {autoDesignCaption.trim() ? (
                  <p className="text-muted-foreground text-sm">
                    Designing a graphic for your {selectedCaptionPlatform} caption.
                  </p>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Write a caption first — the AI will design a graphic that matches it.
                  </p>
                )}
              </DialogHeader>

              <div className="flex flex-col">
                <section className="border-border flex flex-col gap-2 border-b px-6 pb-4 pt-5">
                  <Label>
                    Direction <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Textarea
                    placeholder='e.g. "in the style of a Warhol screenprint"'
                    value={autoDesignPrompt}
                    onChange={(e) => setAutoDesignPrompt(e.target.value)}
                    rows={2}
                  />
                </section>

                <section className="border-border flex flex-col gap-3 border-b px-6 pb-5 pt-5">
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground font-normal">
                      {suggestedReferences ? 'Suggested for this post' : 'Or pick a reference'}
                    </Label>
                    <button
                      type="button"
                      onClick={handleSuggestReferences}
                      disabled={!canSuggest || isLoadingReferences}
                      className="hover:bg-hover text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                      title={
                        canSuggest
                          ? 'Suggest references tailored to this caption'
                          : 'Write a caption to enable suggestions'
                      }>
                      <Sparkles className="h-3 w-3" />
                      {isLoadingReferences
                        ? 'Suggesting…'
                        : suggestedReferences
                          ? 'Refresh'
                          : 'Suggest for this post'}
                    </button>
                  </div>
                  <ol
                    className={cn(
                      'grid grid-cols-2 gap-x-6 gap-y-0.5 transition-opacity',
                      isLoadingReferences && 'opacity-60',
                    )}>
                    {referenceChips.map((chip, i) => (
                      <li key={chip}>
                        <button
                          type="button"
                          onClick={() => setAutoDesignPrompt(`In the style of ${chip}`)}
                          className="hover:bg-hover group flex w-full items-baseline gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors">
                          <span className="text-muted-foreground font-mono text-[10px] tabular-nums">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <span className="text-sm">{chip}</span>
                        </button>
                      </li>
                    ))}
                  </ol>
                </section>

                <section className="border-border grid grid-cols-2 gap-6 border-b px-6 pb-5 pt-5">
                  <div className="flex flex-col gap-2">
                    <Label>
                      Reference image{' '}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <input
                      ref={autoDesignReferenceInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        const base64 = await blobToBase64(file)
                        setAutoDesignReferenceImage(base64)
                        e.target.value = ''
                      }}
                    />
                    {autoDesignReferenceImage ? (
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                          <img
                            src={`data:image/png;base64,${autoDesignReferenceImage}`}
                            className="border-border h-16 w-16 rounded-md border object-contain"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setAutoDesignReferenceImage(null)
                              setAutoDesignUseRefColors(false)
                              setAutoDesignRefFidelity(50)
                            }}>
                            Remove
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="use-ref-colors"
                            checked={autoDesignUseRefColors}
                            onCheckedChange={(checked) =>
                              setAutoDesignUseRefColors(checked === true)
                            }
                          />
                          <label htmlFor="use-ref-colors" className="text-sm">
                            Use reference colors instead of brand palette
                          </label>
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Fidelity</span>
                            <span className="text-muted-foreground text-xs">
                              {autoDesignRefFidelity <= 25
                                ? 'Loose inspiration'
                                : autoDesignRefFidelity <= 50
                                  ? 'Similar layout'
                                  : autoDesignRefFidelity <= 75
                                    ? 'Close match'
                                    : 'Exact copy'}
                            </span>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            step={25}
                            value={autoDesignRefFidelity}
                            onChange={(e) => setAutoDesignRefFidelity(Number(e.target.value))}
                            className="accent-primary h-2 w-full cursor-pointer"
                          />
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-fit"
                        onClick={() => autoDesignReferenceInputRef.current?.click()}>
                        Upload reference
                      </Button>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>What to generate</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {(
                        [
                          { value: 'single', label: 'One design', hint: 'A single graphic' },
                          { value: 'options', label: 'Options', hint: 'A few to pick from' },
                          { value: 'carousel', label: 'Carousel', hint: 'Linked slides' },
                        ] as const
                      ).map((opt) => {
                        const active = autoDesignFormat === opt.value
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setAutoDesignFormat(opt.value)}
                            className={cn(
                              'flex flex-col items-center justify-center gap-0.5 rounded-md border px-2 py-2 text-center transition-colors',
                              active
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:bg-hover',
                            )}>
                            <span
                              className={cn(
                                'text-xs font-medium',
                                active ? 'text-primary' : 'text-foreground',
                              )}>
                              {opt.label}
                            </span>
                            <span className="text-muted-foreground text-[10px]">{opt.hint}</span>
                          </button>
                        )
                      })}
                    </div>
                    {autoDesignFormat === 'options' && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-muted-foreground text-xs">How many</span>
                        <div className="flex gap-1">
                          {[2, 3, 4].map((n) => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setAutoDesignCount(n)}
                              className={cn(
                                'h-7 w-7 rounded-md border text-xs tabular-nums transition-colors',
                                autoDesignCount === n
                                  ? 'border-primary bg-primary/5 text-primary'
                                  : 'border-border text-foreground hover:bg-hover',
                              )}>
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {autoDesignFormat === 'carousel' && (
                      <p className="text-muted-foreground text-[11px] leading-snug">
                        The generator picks the slide count from your caption, so make sure the
                        caption has the points you want to cover.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>Aspect ratio</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {(
                        [
                          { value: '1:1', w: 18, h: 18 },
                          { value: '4:5', w: 16, h: 20 },
                          { value: '16:9', w: 24, h: 14 },
                          { value: '9:16', w: 12, h: 22 },
                        ] as const
                      ).map((opt) => {
                        const active = autoDesignAspectRatio === opt.value
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setAutoDesignAspectRatio(opt.value)}
                            className={cn(
                              'group flex h-14 flex-col items-center justify-center gap-1 rounded-md border transition-colors',
                              active
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:bg-hover',
                            )}>
                            <span
                              className={cn(
                                'block rounded-[2px] transition-colors',
                                active
                                  ? 'bg-primary'
                                  : 'bg-muted-foreground/40 group-hover:bg-muted-foreground',
                              )}
                              style={{ width: opt.w, height: opt.h }}
                            />
                            <span
                              className={cn(
                                'text-[10px] tabular-nums',
                                active ? 'text-primary' : 'text-muted-foreground',
                              )}>
                              {opt.value}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </section>
              </div>

              <div className="border-border flex items-center justify-end border-t px-6 py-4">
                <Button
                  variant="primary"
                  onClick={handleAutoDesignGenerate}
                  disabled={!autoDesignCaption.trim() || isAutoDesignGenerating}>
                  {isAutoDesignGenerating
                    ? 'Generating...'
                    : autoDesignFormat === 'carousel'
                      ? 'Generate Carousel'
                      : autoDesignFormat === 'options'
                        ? `Generate ${autoDesignCount} Options`
                        : 'Generate Design'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

const createImageNode = (
  url: string,
  dimensions: { width: number; height: number },
): LayoutNode => ({
  x: 0,
  y: 0,
  width: dimensions.width,
  height: dimensions.height,
  rotation: 0,
  visible: true,
  data: {
    type: 'image',
    source: { type: 'url', url },
    isLoading: false,
    options: {
      mode: 'cover',
      opacity: 1,
      borderRadius: 0,
      borderWidth: 0,
      borderColor: { r: 0, g: 0, b: 0, a: 255 },
    },
  },
  metadata: { role: 'image' as const },
})

const imageToCanvas = (
  file: FileWithUrl,
  aspectRatio: string = '1:1',
): Extract<WorkspaceMedia, { type: 'canvas' }> => {
  const dimensions = calculateCanvasSize(aspectRatio)
  const imageNode = createImageNode(file.url, dimensions)

  return {
    type: 'canvas',
    nodes: [imageNode],
    aspectRatio,
    file: remotePlaceholderFile,
  }
}

const replaceAtIndex = <T,>(arr: T[], index: number, newItem: T): T[] =>
  arr.map((item, i) => (i === index ? newItem : item))

const remotePlaceholderFile: RemoteFileWithUrl = {
  source: 'remote',
  url: PLACEHOLDER_ASSET_URL,
  key: PLACEHOLDER_ASSET_KEY,
  file: { size: 0, type: 'image/png', name: PLACEHOLDER_ASSET_KEY },
}
